import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  IconBook2,
  IconBuildingStore,
  IconTemplate,
  IconAdjustments,
  IconShieldLock,
  IconAlertTriangle,
  IconKey,
  IconPlus,
  IconTrash,
  IconPencil,
  IconShieldCheck,
  IconUsers,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconLock,
  IconSearch,
  IconMessage2,
  IconDownload,
  IconUpload,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetAdminCustomers,
  usePostAdminCustomer,
  useDeleteAdminCustomerId,
  usePutAdminCustomerId,
  getGetAdminCustomersQueryKey,
  useGetAdminSystemAdmins,
  usePutAdminSystemAdminsId,
  useGetAdminTenantsTenantIdUsers,
  useGetAdminTenantsTenantIdRoles,
  useDeleteAdminUsersId,
  usePostAdminUsersIdPassword,
  usePostAdminTenantsTenantIdUsersUserIdRole,
  getGetAdminTenantsTenantIdUsersQueryKey,
  usePostAdminTenantsTenantIdUsers,
  useGetAdminCatalog,
  usePostAdminCatalog,
  usePutAdminCatalogId,
  useDeleteAdminCatalogId,
  getGetAdminCatalogQueryKey,
  useGetAdminProjectCatalog,
  usePostAdminProjectCatalog,
  usePutAdminProjectCatalogId,
  useDeleteAdminProjectCatalogId,
  getGetAdminProjectCatalogQueryKey,
    useGetAdminLicenses,
    usePostAdminLicenses,
    usePutAdminLicensesId,
    useDeleteAdminLicensesId,
    getGetAdminLicensesQueryKey,
} from '../../api/admin-api/admin-api';
import { useGetAuthUser, usePostAuthUserPassword } from '../../api/auth-api/auth-api';
import {
    CatalogDTO,
    CompanySize,
    CoreMarket,
    CustomerDto,
    IndustrySector,
    LicenseDto,
    LicenseType,
    Methods,
    ProjectCatalogDto,
    TenantUserDto,
    UserDto
} from '../../api/model';
import ConfirmationDialog from '../ui/ConfirmationDialog/ConfirmationDialog';
import RolePermissionsMatrix from '../ManageUsers/RolePermissionsMatrix';
import { useNotifications } from '../../hooks/useNotifications';
import {useCurrentUserPermissions} from '../../hooks/useCurrentUserPermissions';
import AdminFeedbackSection from './AdminFeedbackSection';
import styles from './AdminConsole.module.scss';
import {
  exportCatalogCsv,
  importCatalogCsv,
} from '../../api/admin-catalog-import';
import type {CatalogImportResult} from '../../api/model';

type AdminSection =
    'roles'
    | 'overview'
    | 'catalog'
    | 'organization'
    | 'licenses'
    | 'templates'
    | 'parameters'
    | 'accounts'
    | 'feedback'
    | 'users';
type TranslateFn = TFunction<'settings'>;

interface TenantOption { value: string; label: string; }

const TenantSelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: TenantOption[];
  placeholder: string;
}> = ({ value, onChange, options, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [dropRect, setDropRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selected = options.find(o => o.value === value);

  const handleOpen = () => {
    if (triggerRef.current) {
      setDropRect(triggerRef.current.getBoundingClientRect());
      setOpen(true);
    }
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.tenantSelectTrigger}
        onClick={handleOpen}
      >
        <span className={styles.tenantSelectValue}>
          {selected ? selected.label : placeholder}
        </span>
        <IconChevronDown size={14} className={`${styles.tenantSelectChevron} ${open ? styles.tenantSelectChevronOpen : ''}`} />
      </button>
      {open && dropRect && createPortal(
        <div
          className={styles.tenantSelectDropdown}
          style={{
            position: 'fixed',
            top: dropRect.bottom + 4,
            left: dropRect.left,
            width: dropRect.width,
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          {options.length === 0 && (
            <div className={styles.tenantSelectEmpty}>—</div>
          )}
          {options.map(o => (
            <div
              key={o.value}
              className={`${styles.tenantSelectItem} ${o.value === value ? styles.tenantSelectItemActive : ''}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.label}
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

const OverviewSection: React.FC<{ t: TranslateFn; onNavigate: (s: AdminSection) => void }> = ({ t, onNavigate }) => (
  <div className={styles.section}>
    <div className={styles.warningBanner}>
      <IconAlertTriangle size={14} />
      <span>{t('user.admin.overview.accessWarning')}</span>
    </div>

    <div className={styles.statStrip}>
      <div className={styles.statItem}>
        <span className={styles.statValue}>—</span>
        <span className={styles.statLabel}>{t('user.admin.overview.stats.catalogEntries')}</span>
      </div>
      <div className={styles.statItem}>
        <span className={styles.statValue}>—</span>
        <span className={styles.statLabel}>{t('user.admin.overview.stats.activeOrgs')}</span>
      </div>
      <div className={styles.statItem}>
        <span className={styles.statValue}>—</span>
        <span className={styles.statLabel}>{t('user.admin.overview.stats.templates')}</span>
      </div>
      <div className={styles.statItem}>
        <span className={styles.statValue}>—</span>
        <span className={styles.statLabel}>{t('user.admin.overview.stats.parameters')}</span>
      </div>
    </div>

    <div className={styles.quickNav}>
      <span className={styles.quickNavLabel}>{t('user.admin.overview.quickAccess')}</span>
      <div className={styles.quickLinks}>
          {(['catalog', 'organization', 'licenses', 'users', 'templates', 'parameters'] as AdminSection[]).map((s) => (
          <button key={s} className={styles.quickLink} onClick={() => onNavigate(s)}>
            {t(`user.admin.nav.${s}`)}
          </button>
        ))}
      </div>
    </div>
  </div>
);

type CatalogSubTab = 'checkpoints' | 'projectTypes';
type MethodFilter = 'ALL' | Methods;
type ComplexityFilter = 'ALL' | 'NORMAL' | 'COMPLEX' | 'VERY_COMPLEX';

const PAGE_SIZE = 20;

const downloadCsv = (filename: string, csv: string) => {
  const content = csv.replace(/^\uFEFF/, '');
  const url = URL.createObjectURL(new Blob([`\uFEFF${content}`], {type: 'text/csv;charset=utf-8'}));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

function pageRange(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '…', total];
  if (current >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '…', current - 1, current, current + 1, '…', total];
}

const BLANK_CATALOG: Partial<CatalogDTO> = {
  projectMethodology: Methods.CLASSIC,
  priorityCategoryNormalExtended: true,
  priorityCategoryComplexExtended: false,
  priorityCategoryVeryComplexExtended: false,
};

const BLANK_PT: Partial<ProjectCatalogDto> = {
  industrySector: IndustrySector.GENERAL,
};

const CoverageBar: React.FC<{
  normal?: boolean;
  complex?: boolean;
  veryComplex?: boolean;
}> = ({ normal, complex, veryComplex }) => {
  const segs = [
    { active: !!normal, label: 'Normal', cls: styles.complexitySegNormal },
    { active: !!complex, label: 'Complex', cls: styles.complexitySegComplex },
    { active: !!veryComplex, label: 'Very Complex', cls: styles.complexitySegVeryComplex },
  ];
  const title = `Applies to: ${segs.filter(s => s.active).map(s => s.label).join(' · ') || 'None'}`;
  return (
    <div className={styles.complexityBar} title={title}>
      {segs.map((s, i) => (
        <span key={i} className={`${styles.complexitySeg} ${s.active ? s.cls : ''}`} />
      ))}
    </div>
  );
};



const CatalogSection: React.FC<{ t: TranslateFn }> = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotifications();

  const [subTab, setSubTab] = useState<CatalogSubTab>('checkpoints');
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState<MethodFilter>('ALL');
  const [complexityFilter, setComplexityFilter] = useState<ComplexityFilter>('ALL');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [entryPage, setEntryPage] = useState(1);
  const [ptPage, setPtPage] = useState(1);
  const [dialogLang, setDialogLang] = useState<'en' | 'de'>('en');
  const [ptDialogLang, setPtDialogLang] = useState<'en' | 'de'>('en');
  const importInputRef = useRef<HTMLInputElement>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<CatalogImportResult | null>(null);
  const [importCsv, setImportCsv] = useState<string | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);

  useEffect(() => { setEntryPage(1); }, [search, methodFilter, complexityFilter, phaseFilter, typeFilter]);
  useEffect(() => { setPtPage(1); }, [search]);

  const { data: allEntries = [], isLoading: loadingEntries } = useGetAdminCatalog();
  const createEntry = usePostAdminCatalog();
  const updateEntry = usePutAdminCatalogId();
  const deleteEntry = useDeleteAdminCatalogId();
  const [entryModal, setEntryModal] = useState<{ mode: 'create' | 'edit'; form: Partial<CatalogDTO> } | null>(null);
  const [pendingDeleteEntry, setPendingDeleteEntry] = useState<CatalogDTO | null>(null);

  const { data: allPts = [], isLoading: loadingPts } = useGetAdminProjectCatalog();
  const createPt = usePostAdminProjectCatalog();
  const updatePt = usePutAdminProjectCatalogId();
  const deletePt = useDeleteAdminProjectCatalogId();
  const [ptModal, setPtModal] = useState<{ mode: 'create' | 'edit'; form: Partial<ProjectCatalogDto> } | null>(null);
  const [pendingDeletePt, setPendingDeletePt] = useState<ProjectCatalogDto | null>(null);

  const refreshEntries = () => queryClient.invalidateQueries({ queryKey: getGetAdminCatalogQueryKey() });
  const refreshPts = () => queryClient.invalidateQueries({ queryKey: getGetAdminProjectCatalogQueryKey() });

  const entries = allEntries as CatalogDTO[];
  const pts = allPts as ProjectCatalogDto[];
    const uniquePhases = [...new Set(entries.map(e => e.phaseName || e.phaseNameDe).filter(Boolean) as string[])].sort();
  const uniqueTypes = [...new Set(entries.map(e => e.projectType).filter(Boolean) as string[])].sort();

  const q = search.trim().toLowerCase();
  const hasActiveFilters = !!(search || methodFilter !== 'ALL' || complexityFilter !== 'ALL' || phaseFilter || typeFilter);

  const filteredEntries = entries.filter(e => {
    if (methodFilter !== 'ALL' && e.projectMethodology !== methodFilter) return false;
      if (phaseFilter && (e.phaseName || e.phaseNameDe) !== phaseFilter) return false;
    if (typeFilter && e.projectType !== typeFilter) return false;
    if (complexityFilter === 'NORMAL' && !e.priorityCategoryNormalExtended) return false;
    if (complexityFilter === 'COMPLEX' && !e.priorityCategoryComplexExtended) return false;
    if (complexityFilter === 'VERY_COMPLEX' && !e.priorityCategoryVeryComplexExtended) return false;
      if (q && ![e.taskNameEn, e.taskNameDe, e.phaseName, e.phaseNameDe, e.deliverableNameEn, e.deliverableNameDe, e.projectType, e.validationCriteriaEn, e.validationCriteriaDe, e.responsibleRoles, e.responsibleRolesDe]
      .some(f => f?.toLowerCase().includes(q))) return false;
    return true;
  });

  const filteredPts = pts.filter(p => {
    if (q && ![p.projectTypeEn, p.projectTypeDe, p.projectCharactersEn, p.projectCharactersDe].some(f => f?.toLowerCase().includes(q))) return false;
    return true;
  });

  const clearFilters = () => {
    setSearch(''); setMethodFilter('ALL');
    setComplexityFilter('ALL'); setPhaseFilter(''); setTypeFilter('');
  };

  const closeImport = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportCsv(null);
    if (importInputRef.current) importInputRef.current.value = '';
  };

  const handleExport = async () => {
    setExportBusy(true);
    try {
      const kind = subTab === 'checkpoints' ? 'catalog' : 'project-catalog';
      const csv = await exportCatalogCsv(kind);
      downloadCsv(subTab === 'checkpoints' ? 'checkpoint-catalog.csv' : 'project-catalog.csv', csv);
    } catch {
      showError('Export failed', 'The CSV could not be downloaded. Please try again.');
    } finally {
      setExportBusy(false);
    }
  };

  const handleImportFile = async (file?: File) => {
    if (!file) return;
    setImportFile(file);
    setImportPreview(null);
    setImportBusy(true);
    try {
      const csv = await file.text();
      setImportCsv(csv);
      const preview = await importCatalogCsv(subTab === 'checkpoints' ? 'catalog' : 'project-catalog', csv);
      setImportPreview(preview);
    } catch {
      showError('Import validation failed', 'The CSV could not be validated. Check the file or try again later.');
      closeImport();
    } finally {
      setImportBusy(false);
    }
  };

  const handleImportCommit = async () => {
    if (!importPreview || !importCsv || (importPreview.errors?.length ?? 0) > 0) return;
    setImportBusy(true);
    try {
      const result = await importCatalogCsv(subTab === 'checkpoints' ? 'catalog' : 'project-catalog', importCsv, true);
      if (!result.committed || (result.errors?.length ?? 0) > 0) {
        setImportPreview(result);
        showError('Import not applied', 'The data changed or validation failed. Review the reported errors.');
        return;
      }
      if (subTab === 'checkpoints') await refreshEntries(); else await refreshPts();
      showSuccess('CSV imported', `${result.created ?? 0} created and ${result.updated ?? 0} updated.`);
      closeImport();
    } catch {
      showError('Import failed', 'No changes were applied. Please try again.');
    } finally {
      setImportBusy(false);
    }
  };

  const entryTotalPages = Math.ceil(filteredEntries.length / PAGE_SIZE);
  const pagedEntries = filteredEntries.slice((entryPage - 1) * PAGE_SIZE, entryPage * PAGE_SIZE);

  const ptTotalPages = Math.ceil(filteredPts.length / PAGE_SIZE);
  const pagedPts = filteredPts.slice((ptPage - 1) * PAGE_SIZE, ptPage * PAGE_SIZE);

  const patchEntry = <K extends keyof CatalogDTO>(key: K, val: CatalogDTO[K]) =>
    setEntryModal(m => m ? { ...m, form: { ...m.form, [key]: val } } : m);

  const patchPt = <K extends keyof ProjectCatalogDto>(key: K, val: ProjectCatalogDto[K]) =>
    setPtModal(m => m ? { ...m, form: { ...m.form, [key]: val } } : m);

  const deStatus = (e: CatalogDTO): 'complete' | 'partial' | 'none' => {
      const filled = [e.deliverableNameDe, e.taskNameDe, e.validationCriteriaDe, e.acceptanceCriteriaDe, e.phaseNameDe, e.responsibleRolesDe].filter(Boolean).length;
      return filled === 6 ? 'complete' : filled > 0 ? 'partial' : 'none';
  };

    const entryLabel = (entry?: Pick<CatalogDTO, 'taskNameEn' | 'taskNameDe'> | null) =>
        entry?.taskNameEn || entry?.taskNameDe || 'Entry';

  const saveEntry = () => {
    if (!entryModal) return;
    const form = entryModal.form as CatalogDTO;
    if (entryModal.mode === 'create') {
      createEntry.mutate({ data: form }, {
          onSuccess: () => {
              setEntryModal(null);
              refreshEntries();
              showSuccess('Template created', `"${entryLabel(form)}" added.`);
          },
        onError: () => showError('Create failed', 'Could not create the catalog entry.'),
      });
    } else {
      updateEntry.mutate({ id: form.id!, data: form }, {
          onSuccess: () => {
              setEntryModal(null);
              refreshEntries();
              showSuccess('Template updated', `"${entryLabel(form)}" updated.`);
          },
        onError: () => showError('Update failed', 'Could not update the catalog entry.'),
      });
    }
  };

  const savePt = () => {
    if (!ptModal) return;
    const form = ptModal.form as ProjectCatalogDto;
    if (ptModal.mode === 'create') {
      createPt.mutate({ data: form }, {
        onSuccess: () => { setPtModal(null); refreshPts(); showSuccess('Project type created', `"${form.projectTypeEn || 'Entry'}" added.`); },
        onError: () => showError('Create failed', 'Could not create the project type.'),
      });
    } else {
      updatePt.mutate({ id: form.id!, data: form }, {
        onSuccess: () => { setPtModal(null); refreshPts(); showSuccess('Project type updated', `"${form.projectTypeEn || 'Entry'}" updated.`); },
        onError: () => showError('Update failed', 'Could not update the project type.'),
      });
    }
  };

  const methodLabel = (m?: string) => m ? m.charAt(0) + m.slice(1).toLowerCase() : '—';
  const sectorLabel = (s?: string) => s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—';
  const percentError = (v?: number) => v !== undefined && (v < 0 || v > 100) ? 'Must be between 0 and 100' : undefined;

  return (
    <div className={styles.catalogWrap}>
      <div className={styles.catalogSubNav}>
        <div className={styles.catalogSubTabs}>
          <button type="button" className={`${styles.catalogSubTab} ${subTab === 'checkpoints' ? styles.catalogSubTabActive : ''}`} onClick={() => { setSubTab('checkpoints'); setEntryPage(1); }}>
            Checkpoint Templates
          </button>
          <button type="button" className={`${styles.catalogSubTab} ${subTab === 'projectTypes' ? styles.catalogSubTabActive : ''}`} onClick={() => { setSubTab('projectTypes'); setPtPage(1); }}>
            Project Types
          </button>
        </div>
        <div className={styles.catalogBulkActions}>
          <button type="button" className={styles.btnSecondary} onClick={() => void handleExport()}
                  disabled={exportBusy}>
            <IconDownload size={14}/> {exportBusy ? 'Exporting…' : 'Export CSV'}
          </button>
          <input ref={importInputRef} className={styles.hiddenFileInput} type="file" accept=".csv,text/csv"
                 onChange={e => void handleImportFile(e.target.files?.[0])}/>
          <button type="button" className={styles.btnSecondary} onClick={() => importInputRef.current?.click()}
                  disabled={importBusy}>
            <IconUpload size={14}/> Import CSV
          </button>
          <button type="button" className={styles.btnPrimary} onClick={() => {
            if (subTab === 'checkpoints') {
              setEntryModal({mode: 'create', form: {...BLANK_CATALOG}});
              setDialogLang('en');
            } else {
              setPtModal({mode: 'create', form: {...BLANK_PT}});
              setPtDialogLang('en');
            }
          }}>
            <IconPlus size={14}/>
            {subTab === 'checkpoints' ? 'New Template' : 'New Project Type'}
          </button>
        </div>
      </div>

      {(importFile || importBusy) && (
          <div className={styles.dialogOverlay} onClick={() => {
            if (!importBusy) closeImport();
          }}>
            <div className={`${styles.dialog} ${styles.importDialog}`} onClick={e => e.stopPropagation()}>
              <h4>Import CSV</h4>
              <p className={styles.importDescription}>
                {importBusy && !importPreview ? `Validating ${importFile?.name ?? 'file'}…` : importFile?.name}
              </p>
              {importPreview && (
                  <>
                    <div className={styles.importStats}>
                      <span><strong>{importPreview.created ?? 0}</strong> new</span>
                      <span><strong>{importPreview.updated ?? 0}</strong> updates</span>
                      <span><strong>{importPreview.unchanged ?? 0}</strong> unchanged</span>
                      <span
                          className={(importPreview.errors?.length ?? 0) ? styles.importErrorCount : ''}><strong>{importPreview.failed ?? 0}</strong> errors</span>
                    </div>
                    {(importPreview.errors?.length ?? 0) > 0 && (
                        <div className={styles.importIssues}>
                          {importPreview.errors?.slice(0, 50).map((issue, index) => (
                              <div key={`${issue.row}-${issue.column}-${index}`}>
                                {issue.row ? `Row ${issue.row}` : 'CSV'}{issue.column ? ` · ${issue.column}` : ''}: {issue.message}
                              </div>
                          ))}
                          {(importPreview.errors?.length ?? 0) > 50 &&
                              <div>…and {(importPreview.errors?.length ?? 0) - 50} more errors</div>}
                        </div>
                    )}
                  </>
              )}
              <div className={styles.dialogActions}>
                <button type="button" className={styles.btnSecondary} onClick={closeImport}
                        disabled={importBusy}>Cancel
                </button>
                <button type="button" className={styles.btnPrimary} onClick={() => void handleImportCommit()}
                        disabled={importBusy || !importPreview || (importPreview.errors?.length ?? 0) > 0}>
                  {importBusy && importPreview ? 'Importing…' : 'Apply import'}
                </button>
              </div>
            </div>
          </div>
      )}

      <div className={styles.filterBar}>
        <div className={styles.filterSearch}>
          <IconSearch size={14} className={styles.filterSearchIcon} />
          <input
            className={styles.filterSearchInput}
            placeholder={subTab === 'checkpoints' ? 'Search by task, phase, criteria…' : 'Search project types…'}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {hasActiveFilters && (
            <button type="button" className={styles.clearFiltersBtn} onClick={clearFilters}>Clear</button>
          )}
        </div>
        <div className={styles.filterStrip}>
          {subTab === 'checkpoints' && (
            <>
              <div className={styles.langPills}>
                {(['ALL', Methods.CLASSIC, Methods.HYBRID, Methods.AGILE] as MethodFilter[]).map(m => (
                  <button key={m} type="button" className={`${styles.langPill} ${methodFilter === m ? styles.langPillActive : ''}`} onClick={() => setMethodFilter(m)}>
                    {m === 'ALL' ? 'All' : methodLabel(m)}
                  </button>
                ))}
              </div>
              <div className={styles.filterDivider} />
              <div className={styles.langPills}>
                {([
                  { v: 'ALL' as ComplexityFilter, label: 'Any' },
                  { v: 'NORMAL' as ComplexityFilter, label: 'Normal' },
                  { v: 'COMPLEX' as ComplexityFilter, label: 'Complex' },
                  { v: 'VERY_COMPLEX' as ComplexityFilter, label: 'Very Complex' },
                ]).map(({ v, label }) => (
                  <button key={v} type="button" className={`${styles.langPill} ${complexityFilter === v ? styles.langPillActive : ''}`} onClick={() => setComplexityFilter(v)}>
                    {label}
                  </button>
                ))}
              </div>
              {(uniquePhases.length > 0 || uniqueTypes.length > 0) && <div className={styles.filterDivider} />}
              {uniquePhases.length > 0 && (
                <select className={styles.filterNativeSelect} value={phaseFilter} onChange={e => setPhaseFilter(e.target.value)}>
                  <option value="">All phases</option>
                  {uniquePhases.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              )}
              {uniqueTypes.length > 0 && (
                <select className={styles.filterNativeSelect} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                  <option value="">All types</option>
                  {uniqueTypes.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              )}
            </>
          )}
        </div>
      </div>

      {subTab === 'checkpoints' ? (
        <div className={styles.catalogTable}>
          <div className={styles.catalogTableHead}>
            <span>Task</span>
            <span className={styles.colPath}>Phase → Deliverable</span>
            <span className={styles.colMethod}>Method</span>
            <span className={styles.colCoverage}>Coverage</span>
            <span />
          </div>
          {loadingEntries ? (
            <div className={styles.orgLoading}>Loading templates…</div>
          ) : filteredEntries.length === 0 ? (
            <div className={styles.catalogEmpty}>
              <p>{entries.length === 0 ? 'No checkpoint templates yet.' : 'No results match your filters.'}</p>
              <span>{entries.length === 0 ? 'Add the first one using the button above.' : 'Try adjusting the search or clearing filters.'}</span>
            </div>
          ) : (
            <>
              {pagedEntries.map(entry => (
                <div key={entry.id} className={styles.catalogTableRow}>
                  <div className={styles.entryMain}>
                      <span className={styles.entryTitle}>{entry.taskNameEn || entry.taskNameDe || '—'}</span>
                    {deStatus(entry) !== 'none' && (
                      <span className={`${styles.entryLangBadge} ${deStatus(entry) === 'complete' ? styles.deBadgeComplete : styles.deBadgePartial}`} title={deStatus(entry) === 'complete' ? 'Full DE coverage' : 'Partial DE coverage'}>DE</span>
                    )}
                      {(entry.validationCriteriaEn || entry.validationCriteriaDe) && (
                      <span className={styles.entryPreview}>
                        {(() => {
                            const text = entry.validationCriteriaEn || entry.validationCriteriaDe || '';
                            return text.length > 80 ? text.slice(0, 80) + '…' : text;
                        })()}
                      </span>
                    )}
                  </div>
                  <div className={`${styles.entryPath} ${styles.colPath}`}>
                      {[entry.phaseName || entry.phaseNameDe, entry.deliverableNameEn || entry.deliverableNameDe].filter(Boolean).join(' → ') || '—'}
                  </div>
                  <div className={styles.colMethod}>
                    {entry.projectMethodology && (
                      <span className={`${styles.methodBadge} ${styles[`method${methodLabel(entry.projectMethodology)}`]}`}>
                        {methodLabel(entry.projectMethodology)}
                      </span>
                    )}
                  </div>
                  <div className={styles.colCoverage}>
                    <CoverageBar
                      normal={entry.priorityCategoryNormalExtended}
                      complex={entry.priorityCategoryComplexExtended}
                      veryComplex={entry.priorityCategoryVeryComplexExtended}
                    />
                  </div>
                  <div className={styles.entryActions}>
                    <button type="button" className={styles.adminEditButton} onClick={() => { setEntryModal({ mode: 'edit', form: { ...entry } }); setDialogLang('en'); }} title="Edit"><IconPencil size={14} /></button>
                    <button type="button" className={styles.tenantDeleteButton} onClick={() => setPendingDeleteEntry(entry)} title="Delete"><IconTrash size={14} /></button>
                  </div>
                </div>
              ))}
              {entryTotalPages > 1 && (
                <div className={styles.tablePager}>
                  <span className={styles.pagerInfo}>
                    {(entryPage - 1) * PAGE_SIZE + 1}–{Math.min(entryPage * PAGE_SIZE, filteredEntries.length)} of {filteredEntries.length}
                  </span>
                  <div className={styles.pagerControls}>
                    <button type="button" className={`${styles.pagerBtn} ${entryPage === 1 ? styles.pagerBtnDisabled : ''}`} onClick={() => setEntryPage(p => Math.max(1, p - 1))} disabled={entryPage === 1}>
                      <IconChevronLeft size={13} />
                    </button>
                    {pageRange(entryPage, entryTotalPages).map((p, i) =>
                      p === '…' ? (
                        <span key={`e${i}`} className={styles.pagerEllipsis}>…</span>
                      ) : (
                        <button key={p} type="button" className={`${styles.pagerBtn} ${entryPage === p ? styles.pagerBtnActive : ''}`} onClick={() => setEntryPage(p)}>
                          {p}
                        </button>
                      )
                    )}
                    <button type="button" className={`${styles.pagerBtn} ${entryPage === entryTotalPages ? styles.pagerBtnDisabled : ''}`} onClick={() => setEntryPage(p => Math.min(entryTotalPages, p + 1))} disabled={entryPage === entryTotalPages}>
                      <IconChevronRight size={13} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className={styles.catalogTable}>
          <div className={`${styles.catalogTableHead} ${styles.catalogTableHeadPt}`}>
            <span>Project Type</span>
            <span className={styles.colPtSector}>Sector</span>
            <span className={styles.colPtUid}>UID</span>
            <span />
          </div>
          {loadingPts ? (
            <div className={styles.orgLoading}>Loading project types…</div>
          ) : filteredPts.length === 0 ? (
            <div className={styles.catalogEmpty}>
              <p>{pts.length === 0 ? 'No project types yet.' : 'No results match your filters.'}</p>
              <span>{pts.length === 0 ? 'Add the first one using the button above.' : 'Try adjusting the search or clearing filters.'}</span>
            </div>
          ) : (
            <>
              {pagedPts.map(pt => (
                <div key={pt.id} className={`${styles.catalogTableRow} ${styles.catalogTableRowPt}`}>
                  <div className={styles.entryMain}>
                    <div className={styles.entryTitleRow}>
                      <span className={styles.entryTitle}>{pt.projectTypeEn || pt.projectTypeDe || '—'}</span>
                      {pt.projectTypeDe && (
                        <span className={`${styles.entryLangBadge} ${pt.projectTypeEn && pt.projectCharactersEn ? styles.deBadgeComplete : styles.deBadgePartial}`} title={pt.projectTypeEn && pt.projectCharactersEn ? 'Full DE coverage' : 'Partial DE coverage'}>DE</span>
                      )}
                    </div>
                    {(pt.projectCharactersEn || pt.projectCharactersDe) && (
                      <span className={styles.entryPreview}>
                        {(() => {
                          const text = pt.projectCharactersEn || pt.projectCharactersDe || '';
                          return text.length > 70 ? text.slice(0, 70) + '…' : text;
                        })()}
                      </span>
                    )}
                  </div>
                  <div className={styles.colPtSector}>
                    {pt.industrySector ? <span className={styles.sectorBadge}>{sectorLabel(pt.industrySector)}</span> : <span className={styles.entryEmpty}>—</span>}
                  </div>
                  <div className={`${styles.entryCode} ${styles.colPtUid}`}>{pt.uid || <span className={styles.entryEmpty}>—</span>}</div>
                  <div className={styles.entryActions}>
                    <button type="button" className={styles.adminEditButton} onClick={() => { setPtModal({ mode: 'edit', form: { ...pt } }); setPtDialogLang('en'); }} title="Edit"><IconPencil size={14} /></button>
                    <button type="button" className={styles.tenantDeleteButton} onClick={() => setPendingDeletePt(pt)} title="Delete"><IconTrash size={14} /></button>
                  </div>
                </div>
              ))}
              {ptTotalPages > 1 && (
                <div className={styles.tablePager}>
                  <span className={styles.pagerInfo}>
                    {(ptPage - 1) * PAGE_SIZE + 1}–{Math.min(ptPage * PAGE_SIZE, filteredPts.length)} of {filteredPts.length}
                  </span>
                  <div className={styles.pagerControls}>
                    <button type="button" className={`${styles.pagerBtn} ${ptPage === 1 ? styles.pagerBtnDisabled : ''}`} onClick={() => setPtPage(p => Math.max(1, p - 1))} disabled={ptPage === 1}>
                      <IconChevronLeft size={13} />
                    </button>
                    {pageRange(ptPage, ptTotalPages).map((p, i) =>
                      p === '…' ? (
                        <span key={`pt${i}`} className={styles.pagerEllipsis}>…</span>
                      ) : (
                        <button key={p} type="button" className={`${styles.pagerBtn} ${ptPage === p ? styles.pagerBtnActive : ''}`} onClick={() => setPtPage(p)}>
                          {p}
                        </button>
                      )
                    )}
                    <button type="button" className={`${styles.pagerBtn} ${ptPage === ptTotalPages ? styles.pagerBtnDisabled : ''}`} onClick={() => setPtPage(p => Math.min(ptTotalPages, p + 1))} disabled={ptPage === ptTotalPages}>
                      <IconChevronRight size={13} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {entryModal && (
        <div className={styles.dialogOverlay} onClick={() => setEntryModal(null)}>
          <div className={`${styles.dialog} ${styles.catalogModal}`} onClick={e => e.stopPropagation()}>
            <h4>{entryModal.mode === 'create' ? 'New Checkpoint Template' : 'Edit Checkpoint Template'}</h4>

            <div className={styles.modalSection}>
              <div className={styles.dialogLangRow}>
                <span className={styles.modalSectionTitle}>Content</span>
                <div className={styles.langPills}>
                  <button type="button" className={`${styles.langPill} ${dialogLang === 'en' ? styles.langPillActive : ''}`} onClick={() => setDialogLang('en')}>EN — Required</button>
                  <button type="button" className={`${styles.langPill} ${dialogLang === 'de' ? styles.langPillActive : ''}`} onClick={() => setDialogLang('de')}>DE — Optional</button>
                </div>
              </div>

              {dialogLang === 'de' && (() => {
                const f = entryModal.form;
                  const filled = [f.deliverableNameDe, f.taskNameDe, f.validationCriteriaDe, f.acceptanceCriteriaDe, f.phaseNameDe, f.responsibleRolesDe].filter(Boolean).length;
                  const missing = 6 - filled;
                return (
                    <div
                        className={`${styles.deStatusBanner} ${filled === 6 ? styles.deStatusBannerComplete : filled === 0 ? styles.deStatusBannerNone : styles.deStatusBannerPartial}`}>
                        {filled === 6
                      ? 'All crucial DE fields filled — this entry will be visible to German users.'
                      : filled === 0
                        ? 'No DE fields filled — this entry will be hidden from German users.'
                        : `${missing} crucial DE field${missing > 1 ? 's' : ''} missing — entry will be hidden from German users.`}
                  </div>
                );
              })()}

              {dialogLang === 'en' ? (
                <>
                  <div className={styles.modalFieldGrid}>
                    <div className={styles.dialogField}>
                      <label>Task Name *</label>
                      <input className={styles.dialogInput} value={entryModal.form.taskNameEn ?? ''} onChange={e => patchEntry('taskNameEn', e.target.value)} placeholder="e.g. Quality Review" />
                    </div>
                    <div className={styles.dialogField}>
                      <label>Deliverable Name *</label>
                      <input className={styles.dialogInput} value={entryModal.form.deliverableNameEn ?? ''} onChange={e => patchEntry('deliverableNameEn', e.target.value)} placeholder="e.g. Concept Document" />
                    </div>
                  </div>
                  <div className={styles.dialogField}>
                    <label>Validation Criteria *</label>
                    <textarea className={`${styles.dialogInput} ${styles.modalTextarea}`} value={entryModal.form.validationCriteriaEn ?? ''} onChange={e => patchEntry('validationCriteriaEn', e.target.value)} placeholder="What must be validated?" />
                  </div>
                  <div className={styles.dialogField}>
                    <label>Acceptance Criteria *</label>
                    <textarea className={`${styles.dialogInput} ${styles.modalTextarea}`} value={entryModal.form.acceptanceCriteriaEn ?? ''} onChange={e => patchEntry('acceptanceCriteriaEn', e.target.value)} placeholder="What must be accepted?" />
                  </div>
                  <div className={styles.dialogField}>
                    <label>Validation Methods</label>
                    <textarea className={`${styles.dialogInput} ${styles.modalTextarea}`} value={entryModal.form.validationMethodsEn ?? ''} onChange={e => patchEntry('validationMethodsEn', e.target.value)} placeholder="How will it be validated?" />
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.modalFieldGrid}>
                    <div className={styles.dialogField}>
                      <label>Task Name</label>
                      <input className={styles.dialogInput} value={entryModal.form.taskNameDe ?? ''} onChange={e => patchEntry('taskNameDe', e.target.value)} placeholder="z. B. Qualitätsprüfung" />
                    </div>
                    <div className={styles.dialogField}>
                      <label>Deliverable Name</label>
                      <input className={styles.dialogInput} value={entryModal.form.deliverableNameDe ?? ''} onChange={e => patchEntry('deliverableNameDe', e.target.value)} placeholder="z. B. Konzeptdokument" />
                    </div>
                  </div>
                  <div className={styles.dialogField}>
                    <label>Validation Criteria</label>
                    <textarea className={`${styles.dialogInput} ${styles.modalTextarea}`} value={entryModal.form.validationCriteriaDe ?? ''} onChange={e => patchEntry('validationCriteriaDe', e.target.value)} placeholder="Was muss validiert werden?" />
                  </div>
                  <div className={styles.dialogField}>
                    <label>Acceptance Criteria</label>
                    <textarea className={`${styles.dialogInput} ${styles.modalTextarea}`} value={entryModal.form.acceptanceCriteriaDe ?? ''} onChange={e => patchEntry('acceptanceCriteriaDe', e.target.value)} placeholder="Was muss akzeptiert werden?" />
                  </div>
                  <div className={styles.dialogField}>
                    <label>Validation Methods</label>
                    <textarea className={`${styles.dialogInput} ${styles.modalTextarea}`} value={entryModal.form.validationMethodsDe ?? ''} onChange={e => patchEntry('validationMethodsDe', e.target.value)} placeholder="Wie wird validiert?" />
                  </div>
                </>
              )}
            </div>

            <div className={styles.modalSection}>
              <span className={styles.modalSectionTitle}>Basic Info</span>
              <div className={styles.modalFieldGrid}>
                <div className={styles.dialogField}>
                  <label>Methodology</label>
                  <select className={styles.dialogInput} value={entryModal.form.projectMethodology ?? Methods.CLASSIC} onChange={e => patchEntry('projectMethodology', e.target.value as Methods)}>
                    <option value={Methods.CLASSIC}>Classic</option>
                    <option value={Methods.HYBRID}>Hybrid</option>
                    <option value={Methods.AGILE}>Agile</option>
                  </select>
                </div>
                <div className={styles.dialogField}>
                  <label>Project Type</label>
                  <input className={styles.dialogInput} value={entryModal.form.projectType ?? ''} onChange={e => patchEntry('projectType', e.target.value)} placeholder="e.g. Software Development" />
                </div>
                <div className={styles.dialogField}>
                    <label>Phase Name {dialogLang === 'de' ? '(DE)' : '(EN)'}</label>
                    <input
                        className={styles.dialogInput}
                        value={dialogLang === 'de' ? (entryModal.form.phaseNameDe ?? '') : (entryModal.form.phaseName ?? '')}
                        onChange={e => patchEntry(dialogLang === 'de' ? 'phaseNameDe' : 'phaseName', e.target.value)}
                        placeholder={dialogLang === 'de' ? 'z. B. Planung' : 'e.g. Planning'}
                    />
                </div>
                <div className={`${styles.dialogField} ${styles.modalFieldFull}`}>
                    <label>Responsible Roles {dialogLang === 'de' ? '(DE)' : '(EN)'}</label>
                    <input
                        className={styles.dialogInput}
                        value={dialogLang === 'de' ? (entryModal.form.responsibleRolesDe ?? '') : (entryModal.form.responsibleRoles ?? '')}
                        onChange={e => patchEntry(dialogLang === 'de' ? 'responsibleRolesDe' : 'responsibleRoles', e.target.value)}
                        placeholder={dialogLang === 'de' ? 'z. B. Projektleiter, QA Lead' : 'e.g. Project Manager, QA Lead'}
                    />
                </div>
              </div>
            </div>

            <div className={styles.modalSection}>
              <span className={styles.modalSectionTitle}>Applies to Complexity</span>
              <div className={styles.complexityToggles}>
                <button type="button" className={`${styles.toggleChip} ${entryModal.form.priorityCategoryNormalExtended ? styles.toggleChipNormal : ''}`} onClick={() => patchEntry('priorityCategoryNormalExtended', !entryModal.form.priorityCategoryNormalExtended)}>Normal</button>
                <button type="button" className={`${styles.toggleChip} ${entryModal.form.priorityCategoryComplexExtended ? styles.toggleChipComplex : ''}`} onClick={() => patchEntry('priorityCategoryComplexExtended', !entryModal.form.priorityCategoryComplexExtended)}>Complex</button>
                <button type="button" className={`${styles.toggleChip} ${entryModal.form.priorityCategoryVeryComplexExtended ? styles.toggleChipVeryComplex : ''}`} onClick={() => patchEntry('priorityCategoryVeryComplexExtended', !entryModal.form.priorityCategoryVeryComplexExtended)}>Very Complex</button>
              </div>
            </div>

            <div className={styles.modalSection}>
              <span className={styles.modalSectionTitle}>Timing & Order</span>
              <div className={styles.modalFieldGrid}>
                <div className={styles.dialogField}>
                  <label>Buffer %</label>
                  <input type="number" min={0} max={100} className={`${styles.dialogInput} ${percentError(entryModal.form.bufferTimePercentage) ? styles.dialogInputError : ''}`} value={entryModal.form.bufferTimePercentage ?? ''} onChange={e => patchEntry('bufferTimePercentage', e.target.value ? Number(e.target.value) : undefined)} />
                  {percentError(entryModal.form.bufferTimePercentage) && <span className={styles.fieldErrorMsg}>{percentError(entryModal.form.bufferTimePercentage)}</span>}
                </div>
                <div className={styles.dialogField}>
                  <label>Lead Time %</label>
                  <input type="number" min={0} max={100} className={`${styles.dialogInput} ${percentError(entryModal.form.leadTimePercentage) ? styles.dialogInputError : ''}`} value={entryModal.form.leadTimePercentage ?? ''} onChange={e => patchEntry('leadTimePercentage', e.target.value ? Number(e.target.value) : undefined)} />
                  {percentError(entryModal.form.leadTimePercentage) && <span className={styles.fieldErrorMsg}>{percentError(entryModal.form.leadTimePercentage)}</span>}
                </div>
                <div className={styles.dialogField}>
                  <label>Reserve %</label>
                  <input type="number" min={0} max={100} className={`${styles.dialogInput} ${percentError(entryModal.form.reservePercentage) ? styles.dialogInputError : ''}`} value={entryModal.form.reservePercentage ?? ''} onChange={e => patchEntry('reservePercentage', e.target.value ? Number(e.target.value) : undefined)} />
                  {percentError(entryModal.form.reservePercentage) && <span className={styles.fieldErrorMsg}>{percentError(entryModal.form.reservePercentage)}</span>}
                </div>
                <div className={styles.dialogField}>
                  <label>Order in Phase</label>
                  <input type="number" min={1} className={styles.dialogInput} value={entryModal.form.deliverObjectOrderPerPhase ?? ''} onChange={e => patchEntry('deliverObjectOrderPerPhase', e.target.value ? Number(e.target.value) : undefined)} />
                </div>
              </div>
            </div>

            <div className={styles.modalSection}>
              <span className={styles.modalSectionTitle}>Advanced</span>
              <div className={styles.modalFieldGrid}>
                <div className={styles.dialogField}>
                  <label>Predecessor ID</label>
                  <input className={styles.dialogInput} value={entryModal.form.predecessorCheckpointId ?? ''} onChange={e => patchEntry('predecessorCheckpointId', e.target.value)} />
                </div>
                <div className={styles.dialogField}>
                  <label>Dependency Type</label>
                  <input className={styles.dialogInput} value={entryModal.form.dependencyType ?? ''} onChange={e => patchEntry('dependencyType', e.target.value)} placeholder="e.g. hard, soft, optional" />
                </div>
              </div>
              <div className={styles.dialogField}>
                <label>Comments</label>
                <textarea className={`${styles.dialogInput} ${styles.modalTextarea}`} value={entryModal.form.comments ?? ''} onChange={e => patchEntry('comments', e.target.value)} />
              </div>
            </div>

            <div className={styles.dialogActions}>
              <button className={styles.discardButton} onClick={() => setEntryModal(null)}>Cancel</button>
              <button
                className={styles.saveButton}
                onClick={saveEntry}
                disabled={
                  !entryModal.form.taskNameEn?.trim() ||
                  !entryModal.form.deliverableNameEn?.trim() ||
                  !entryModal.form.validationCriteriaEn?.trim() ||
                  !entryModal.form.acceptanceCriteriaEn?.trim() ||
                  !!percentError(entryModal.form.bufferTimePercentage) ||
                  !!percentError(entryModal.form.leadTimePercentage) ||
                  !!percentError(entryModal.form.reservePercentage) ||
                  createEntry.isPending || updateEntry.isPending
                }
              >
                {(createEntry.isPending || updateEntry.isPending) ? 'Saving…' : entryModal.mode === 'create' ? 'Create Template' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {ptModal && (
        <div className={styles.dialogOverlay} onClick={() => setPtModal(null)}>
          <div className={styles.dialog} onClick={e => e.stopPropagation()}>
            <h4>{ptModal.mode === 'create' ? 'New Project Type' : 'Edit Project Type'}</h4>

            <div className={styles.dialogField}>
              <label>Industry Sector</label>
              <select className={styles.dialogInput} value={ptModal.form.industrySector ?? IndustrySector.GENERAL} onChange={e => patchPt('industrySector', e.target.value as IndustrySector)}>
                {Object.values(IndustrySector).map(s => <option key={s} value={s}>{sectorLabel(s)}</option>)}
              </select>
            </div>

            <div className={styles.dialogField}>
              <label>UID</label>
              <input className={styles.dialogInput} value={ptModal.form.uid ?? ''} onChange={e => patchPt('uid', e.target.value)} placeholder="e.g. B02" />
            </div>

            <div className={styles.dialogField}>
              <div className={styles.dialogLangRow}>
                <span>Project Type</span>
                <div>
                  <button type="button" className={`${styles.langPill} ${ptDialogLang === 'en' ? styles.langPillActive : ''}`} onClick={() => setPtDialogLang('en')}>EN — Required</button>
                  <button type="button" className={`${styles.langPill} ${ptDialogLang === 'de' ? styles.langPillActive : ''}`} onClick={() => setPtDialogLang('de')}>DE — Optional</button>
                </div>
              </div>
              {ptDialogLang === 'en' ? (
                <input className={styles.dialogInput} value={ptModal.form.projectTypeEn ?? ''} onChange={e => patchPt('projectTypeEn', e.target.value)} placeholder="e.g. Software Development" />
              ) : (
                <input className={styles.dialogInput} value={ptModal.form.projectTypeDe ?? ''} onChange={e => patchPt('projectTypeDe', e.target.value)} placeholder="z.B. Softwareentwicklung" />
              )}
            </div>

            <div className={styles.dialogField}>
              <div className={styles.dialogLangRow}>
                <span>Project Characters</span>
                <div>
                  <button type="button" className={`${styles.langPill} ${ptDialogLang === 'en' ? styles.langPillActive : ''}`} onClick={() => setPtDialogLang('en')}>EN — Required</button>
                  <button type="button" className={`${styles.langPill} ${ptDialogLang === 'de' ? styles.langPillActive : ''}`} onClick={() => setPtDialogLang('de')}>DE — Optional</button>
                </div>
              </div>
              {ptDialogLang === 'en' ? (
                <textarea className={`${styles.dialogInput} ${styles.modalTextarea}`} value={ptModal.form.projectCharactersEn ?? ''} onChange={e => patchPt('projectCharactersEn', e.target.value)} placeholder="Describe the characteristics…" />
              ) : (
                <textarea className={`${styles.dialogInput} ${styles.modalTextarea}`} value={ptModal.form.projectCharactersDe ?? ''} onChange={e => patchPt('projectCharactersDe', e.target.value)} placeholder="Beschreiben Sie die Merkmale…" />
              )}
            </div>

            <div className={styles.dialogActions}>
              <button className={styles.discardButton} onClick={() => setPtModal(null)}>Cancel</button>
              <button className={styles.saveButton} onClick={savePt} disabled={!ptModal.form.projectTypeEn?.trim() || createPt.isPending || updatePt.isPending}>
                {(createPt.isPending || updatePt.isPending) ? 'Saving…' : ptModal.mode === 'create' ? 'Create' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={!!pendingDeleteEntry}
        onClose={() => setPendingDeleteEntry(null)}
        onConfirm={() => {
          if (!pendingDeleteEntry?.id) return;
          deleteEntry.mutate({ id: pendingDeleteEntry.id }, {
              onSuccess: () => {
                  setPendingDeleteEntry(null);
                  refreshEntries();
                  showSuccess('Template deleted', `"${entryLabel(pendingDeleteEntry)}" removed.`);
              },
            onError: () => showError('Delete failed', 'Could not delete the template.'),
          });
        }}
        type="danger"
        title="Delete template?"
        message={`Remove "${pendingDeleteEntry ? entryLabel(pendingDeleteEntry) : 'this template'}" from the catalog? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
      <ConfirmationDialog
        isOpen={!!pendingDeletePt}
        onClose={() => setPendingDeletePt(null)}
        onConfirm={() => {
          if (!pendingDeletePt?.id) return;
          deletePt.mutate({ id: pendingDeletePt.id }, {
            onSuccess: () => { setPendingDeletePt(null); refreshPts(); showSuccess('Project type deleted', `"${pendingDeletePt.projectTypeEn || 'Entry'}" removed.`); },
            onError: () => showError('Delete failed', 'Could not delete the project type.'),
          });
        }}
        type="danger"
        title="Delete project type?"
        message={`Remove "${pendingDeletePt?.projectTypeEn || 'this project type'}"? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

const companySizeLabels: Record<CompanySize, string> = {
  [CompanySize.SMALL_COMPANY]: 'Small Company',
  [CompanySize.MEDIUM_COMPANY]: 'Medium Company',
  [CompanySize.LARGE_COMPANY]: 'Large Company',
};

const coreMarketLabels: Record<CoreMarket, string> = {
  [CoreMarket.CH]: 'Switzerland (CH)',
  [CoreMarket.DE]: 'Germany (DE)',
  [CoreMarket.EU]: 'Europe (EU)',
  [CoreMarket.US]: 'United States (US)',
  [CoreMarket.GLOBAL]: 'Global',
};

const industrySectorLabels: Record<IndustrySector, string> = {
  [IndustrySector.BANK]: 'Banking',
  [IndustrySector.INSURANCE]: 'Insurance',
  [IndustrySector.ENERGY_SUPPLY]: 'Energy Supply',
  [IndustrySector.TRANSPORT]: 'Transport',
  [IndustrySector.LIFE_SCIENCE]: 'Life Science',
  [IndustrySector.TELECOMMUNICATION]: 'Telecommunication',
  [IndustrySector.IT]: 'IT / Technology',
  [IndustrySector.CONSTRUCTION]: 'Construction',
  [IndustrySector.TRADE_AND_CONSUMER_GOODS]: 'Trade & Consumer Goods',
  [IndustrySector.HEALTHCARE]: 'Healthcare',
  [IndustrySector.GOVERNMENT_AGENCIES]: 'Government / Public Sector',
  [IndustrySector.GENERAL]: 'General',
};

const licenseTypeLabels: Record<LicenseType, string> = {
  [LicenseType.PILOT]: 'Pilot',
  [LicenseType.BASIC]: 'Basic',
  [LicenseType.PRO]: 'Professional',
  [LicenseType.ENTERPRISE]: 'Enterprise',
};

const emptyTenantForm = {
  name: '',
  companySize: CompanySize.SMALL_COMPANY as CompanySize,
  coreMarket: CoreMarket.CH as CoreMarket,
  industrySector: IndustrySector.GENERAL as IndustrySector,
  licenseType: LicenseType.BASIC as LicenseType,
  licenseExpirationDate: '',
  active: true,
};

const emptyEditForm = {
  name: '',
  companySize: CompanySize.SMALL_COMPANY as CompanySize,
  coreMarket: CoreMarket.CH as CoreMarket,
  industrySector: IndustrySector.GENERAL as IndustrySector,
  licenseId: undefined as number | undefined,
  licenseExpirationDate: '',
  active: true,
};

const OrganizationSection: React.FC<{ t: TranslateFn }> = ({ t }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotifications();
  const { data: tenants, isLoading } = useGetAdminCustomers();
  const { data: licenses = [] } = useGetAdminLicenses();
  const createMutation = usePostAdminCustomer();
  const deleteMutation = useDeleteAdminCustomerId();
  const renameMutation = usePutAdminCustomerId();

  const [createOpen, setCreateOpen] = useState(false);
  const [tenantConfirmOpen, setTenantConfirmOpen] = useState(false);
  const [tenantForm, setTenantForm] = useState(emptyTenantForm);
  const [tenantErrors, setTenantErrors] = useState<Record<string, string>>({});
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [deleteStep1, setDeleteStep1] = useState(false);
  const [deleteStep2, setDeleteStep2] = useState(false);
  const [editingTenant, setEditingTenant] = useState<CustomerDto | null>(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});

  const refresh = () => queryClient.invalidateQueries({ queryKey: getGetAdminCustomersQueryKey() });

  const openEdit = (tenant: CustomerDto) => {
    setEditingTenant(tenant);
    setEditForm({
      name: tenant.name ?? '',
      companySize: tenant.companySize ?? emptyEditForm.companySize,
      coreMarket: tenant.coreMarket ?? emptyEditForm.coreMarket,
      industrySector: tenant.industrySector ?? emptyEditForm.industrySector,
      licenseId: tenant.licenseId ?? licenses[0]?.id,
      licenseExpirationDate: tenant.licenseExpirationDate ? tenant.licenseExpirationDate.slice(0, 10) : '',
      active: tenant.active ?? true,
    });
    setEditErrors({});
  };

  const handleEditSave = () => {
    if (!editingTenant) return;
    const errs: Record<string, string> = {};
    if (!editForm.name.trim()) errs.name = t('user.admin.organization.tenants.validation.nameRequired', 'Company name is required');
    else if (editForm.name.trim().length < 2) errs.name = t('user.admin.organization.tenants.validation.nameMin', 'Must be at least 2 characters');
    setEditErrors(errs);
    if (Object.keys(errs).length > 0) return;

    renameMutation.mutate(
      {
        id: editingTenant.id!,
        data: {
          ...editingTenant,
          name: editForm.name.trim(),
          companySize: editForm.companySize,
          coreMarket: editForm.coreMarket,
          industrySector: editForm.industrySector,
          licenseId: editForm.licenseId,
          licenseExpirationDate: editForm.licenseExpirationDate
            ? (`${editForm.licenseExpirationDate}T00:00:00` as any)
            : undefined,
          active: editForm.active,
        },
      },
      {
        onSuccess: () => {
          setEditingTenant(null);
          refresh();
          showSuccess('Tenant updated', `"${editForm.name.trim()}" has been updated.`);
        },
        onError: () => showError('Update failed', 'Could not update the tenant.'),
      }
    );
  };

  const resetTenantForm = () => {
    setTenantForm(emptyTenantForm);
    setTenantErrors({});
  };

  const handleTenantSubmit = () => {
    const errs: Record<string, string> = {};
    if (!tenantForm.name.trim()) errs.name = t('user.admin.organization.tenants.validation.nameRequired', 'Company name is required');
    else if (tenantForm.name.trim().length < 2) errs.name = t('user.admin.organization.tenants.validation.nameMin', 'Must be at least 2 characters');
    setTenantErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setTenantConfirmOpen(true);
  };

  const doCreateTenant = () => {
    createMutation.mutate(
      {
        data: {
          name: tenantForm.name.trim(),
          licenseId: 1,
          companySize: tenantForm.companySize,
          coreMarket: tenantForm.coreMarket,
          industrySector: tenantForm.industrySector,
          licenseType: tenantForm.licenseType,
          licenseExpirationDate: tenantForm.licenseExpirationDate
            ? (`${tenantForm.licenseExpirationDate}T00:00:00` as any)
            : undefined,
          active: tenantForm.active,
        },
      },
      {
        onSuccess: () => {
          const name = tenantForm.name.trim();
          resetTenantForm();
          setCreateOpen(false);
          setTenantConfirmOpen(false);
          refresh();
          showSuccess('Tenant created', `"${name}" has been created.`);
        },
        onError: () => showError('Create failed', 'Could not create the tenant.'),
      }
    );
  };


  return (
    <div className={styles.section}>
      <div className={styles.orgHead}>
        <p className={styles.sectionIntroText}>{t('user.admin.organization.description')}</p>
        <button className={styles.btnPrimary} onClick={() => setCreateOpen(true)}>
          <IconPlus size={14} />
          {t('user.admin.organization.tenants.new', 'New tenant')}
        </button>
      </div>

      {isLoading ? (
        <div className={styles.orgLoading}>{t('user.admin.organization.tenants.loading', 'Loading tenants…')}</div>
      ) : (
        <div className={styles.tenantList}>
          {(tenants ?? []).map(tenant => (
            <div key={tenant.id} className={styles.tenantRow}>
              <div className={styles.tenantInfo}>
                <span className={styles.tenantName}>{tenant.name}</span>
                <span className={styles.tenantId}>{tenant.tenantId}</span>
              </div>
              <div className={styles.tenantMeta}>
                <span className={`${styles.tenantStatus} ${tenant.active ? styles.tenantActive : ''}`}>
                  {tenant.active ? t('user.admin.organization.tenants.active', 'Active') : t('user.admin.organization.tenants.inactive', 'Inactive')}
                </span>
              </div>
              <button
                className={styles.adminEditButton}
                onClick={() => openEdit(tenant)}
                title={t('user.admin.organization.tenants.rename', 'Edit tenant')}
              >
                <IconPencil size={14} />
              </button>
              <button
                className={styles.tenantDeleteButton}
                onClick={() => { setPendingDeleteId(tenant.id ?? null); setDeleteStep1(true); }}
                title={t('user.admin.organization.tenants.delete', 'Delete tenant')}
              >
                <IconTrash size={14} />
              </button>
            </div>
          ))}
          {(tenants ?? []).length === 0 && (
            <div className={styles.orgEmpty}>{t('user.admin.organization.tenants.empty', 'No tenants yet.')}</div>
          )}
        </div>
      )}

      {createOpen && (
        <div className={styles.dialogOverlay} onClick={() => { resetTenantForm(); setCreateOpen(false); }}>
          <div className={styles.dialog} style={{ width: 520 }} onClick={e => e.stopPropagation()}>
            <h4>{t('user.admin.organization.tenants.newTitle', 'Create tenant')}</h4>

            <div className={styles.dialogField}>
              <label>{t('user.admin.organization.tenants.nameLabel', 'Company name')} *</label>
              <input
                className={`${styles.dialogInput} ${tenantErrors.name ? styles.dialogInputError : ''}`}
                value={tenantForm.name}
                onChange={e => { setTenantForm(f => ({ ...f, name: e.target.value })); if (tenantErrors.name) setTenantErrors(p => ({ ...p, name: '' })); }}
                placeholder={t('user.admin.organization.tenants.namePlaceholder', 'Enter company name')}
                autoFocus
              />
              {tenantErrors.name && <span className={styles.fieldErrorMsg}>{tenantErrors.name}</span>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className={styles.dialogField}>
                <label>{t('user.admin.organization.tenants.fields.companySize', 'Company size')}</label>
                <select
                  className={styles.dialogInput}
                  value={tenantForm.companySize}
                  onChange={e => setTenantForm(f => ({ ...f, companySize: e.target.value as CompanySize }))}
                >
                  {Object.values(CompanySize).map(v => (
                    <option key={v} value={v}>{t(`user.admin.organization.tenants.companySize.${v}`, companySizeLabels[v])}</option>
                  ))}
                </select>
              </div>
              <div className={styles.dialogField}>
                <label>{t('user.admin.organization.tenants.fields.coreMarket', 'Core market')}</label>
                <select
                  className={styles.dialogInput}
                  value={tenantForm.coreMarket}
                  onChange={e => setTenantForm(f => ({ ...f, coreMarket: e.target.value as CoreMarket }))}
                >
                  {Object.values(CoreMarket).map(v => (
                    <option key={v} value={v}>{t(`user.admin.organization.tenants.coreMarket.${v}`, coreMarketLabels[v])}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.dialogField}>
              <label>{t('user.admin.organization.tenants.fields.industrySector', 'Industry sector')}</label>
              <select
                className={styles.dialogInput}
                value={tenantForm.industrySector}
                onChange={e => setTenantForm(f => ({ ...f, industrySector: e.target.value as IndustrySector }))}
              >
                {Object.values(IndustrySector).map(v => (
                  <option key={v} value={v}>{t(`user.admin.organization.tenants.industrySector.${v}`, industrySectorLabels[v])}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className={styles.dialogField}>
                <label>{t('user.admin.organization.tenants.fields.licenseType', 'License type')}</label>
                <select
                  className={styles.dialogInput}
                  value={tenantForm.licenseType}
                  onChange={e => setTenantForm(f => ({ ...f, licenseType: e.target.value as LicenseType }))}
                >
                  {Object.values(LicenseType).map(v => (
                    <option key={v} value={v}>{t(`user.admin.organization.tenants.licenseType.${v}`, licenseTypeLabels[v])}</option>
                  ))}
                </select>
              </div>
              <div className={styles.dialogField}>
                <label>{t('user.admin.organization.tenants.fields.licenseExpiry', 'License expiry date')}</label>
                <input
                  type="date"
                  className={styles.dialogInput}
                  value={tenantForm.licenseExpirationDate}
                  onChange={e => setTenantForm(f => ({ ...f, licenseExpirationDate: e.target.value }))}
                />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                id="tenantActive"
                checked={tenantForm.active}
                onChange={e => setTenantForm(f => ({ ...f, active: e.target.checked }))}
                style={{ width: 15, height: 15, cursor: 'pointer' }}
              />
              <label htmlFor="tenantActive" style={{ fontSize: 13, fontWeight: 500, cursor: 'pointer', marginBottom: 0 }}>
                {t('user.admin.organization.tenants.activeLabel', 'Active tenant')}
              </label>
            </div>

            <div className={styles.dialogActions}>
              <button className={styles.discardButton} onClick={() => { resetTenantForm(); setCreateOpen(false); }}>
                {t('user.permissions.edit.discard', 'Cancel')}
              </button>
              <button className={styles.saveButton} onClick={handleTenantSubmit}>
                {t('user.admin.organization.tenants.reviewCreate', 'Review & Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {tenantConfirmOpen && (
        <div className={styles.dialogOverlay} onClick={() => setTenantConfirmOpen(false)}>
          <div className={styles.dialog} style={{ width: 480 }} onClick={e => e.stopPropagation()}>
            <h4>{t('user.admin.organization.tenants.confirmTitle', 'Confirm tenant creation')}</h4>
            <div style={{
              background: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: 8,
              padding: '14px 16px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px 24px',
            }}>
              <div><span style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>{t('user.admin.organization.tenants.confirmLabels.company', 'Company')}</span><p style={{ margin: '3px 0 0', fontSize: 13.5, fontWeight: 600 }}>{tenantForm.name.trim()}</p></div>
              <div><span style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>{t('user.admin.organization.tenants.confirmLabels.size', 'Size')}</span><p style={{ margin: '3px 0 0', fontSize: 13.5 }}>{t(`user.admin.organization.tenants.companySize.${tenantForm.companySize}`, companySizeLabels[tenantForm.companySize])}</p></div>
              <div><span style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>{t('user.admin.organization.tenants.confirmLabels.coreMarket', 'Core market')}</span><p style={{ margin: '3px 0 0', fontSize: 13.5 }}>{t(`user.admin.organization.tenants.coreMarket.${tenantForm.coreMarket}`, coreMarketLabels[tenantForm.coreMarket])}</p></div>
              <div><span style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>{t('user.admin.organization.tenants.confirmLabels.industry', 'Industry')}</span><p style={{ margin: '3px 0 0', fontSize: 13.5 }}>{t(`user.admin.organization.tenants.industrySector.${tenantForm.industrySector}`, industrySectorLabels[tenantForm.industrySector])}</p></div>
              <div><span style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>{t('user.admin.organization.tenants.confirmLabels.license', 'License')}</span><p style={{ margin: '3px 0 0', fontSize: 13.5 }}>{t(`user.admin.organization.tenants.licenseType.${tenantForm.licenseType}`, licenseTypeLabels[tenantForm.licenseType])}</p></div>
              <div><span style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>{t('user.admin.organization.tenants.confirmLabels.expiry', 'Expiry')}</span><p style={{ margin: '3px 0 0', fontSize: 13.5 }}>{tenantForm.licenseExpirationDate || t('user.admin.organization.tenants.confirmLabels.noExpiry', '—')}</p></div>
              <div><span style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>{t('user.admin.organization.tenants.confirmLabels.status', 'Status')}</span><p style={{ margin: '3px 0 0', fontSize: 13.5 }}>{tenantForm.active ? t('user.admin.organization.tenants.active', 'Active') : t('user.admin.organization.tenants.inactive', 'Inactive')}</p></div>
            </div>
            <div className={styles.dialogActions}>
              <button className={styles.discardButton} onClick={() => setTenantConfirmOpen(false)}>
                {t('user.admin.organization.tenants.confirmBack', 'Back')}
              </button>
              <button
                className={styles.saveButton}
                onClick={doCreateTenant}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? t('user.admin.organization.tenants.creating', 'Creating…') : t('user.admin.organization.tenants.confirmCreate', 'Create tenant')}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingTenant && (
        <div className={styles.dialogOverlay} onClick={() => setEditingTenant(null)}>
          <div className={styles.dialog} style={{ width: 520 }} onClick={e => e.stopPropagation()}>
            <h4>{t('user.admin.organization.tenants.renameTitle', 'Edit tenant')}</h4>

            <div className={styles.dialogField}>
              <label>{t('user.admin.organization.tenants.nameLabel', 'Company name')} *</label>
              <input
                className={`${styles.dialogInput} ${editErrors.name ? styles.dialogInputError : ''}`}
                value={editForm.name}
                onChange={e => { setEditForm(f => ({ ...f, name: e.target.value })); if (editErrors.name) setEditErrors(p => ({ ...p, name: '' })); }}
                autoFocus
              />
              {editErrors.name && <span className={styles.fieldErrorMsg}>{editErrors.name}</span>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className={styles.dialogField}>
                <label>{t('user.admin.organization.tenants.fields.companySize', 'Company size')}</label>
                <select
                  className={styles.dialogInput}
                  value={editForm.companySize}
                  onChange={e => setEditForm(f => ({ ...f, companySize: e.target.value as CompanySize }))}
                >
                  {Object.values(CompanySize).map(v => (
                    <option key={v} value={v}>{t(`user.admin.organization.tenants.companySize.${v}`, companySizeLabels[v])}</option>
                  ))}
                </select>
              </div>
              <div className={styles.dialogField}>
                <label>{t('user.admin.organization.tenants.fields.coreMarket', 'Core market')}</label>
                <select
                  className={styles.dialogInput}
                  value={editForm.coreMarket}
                  onChange={e => setEditForm(f => ({ ...f, coreMarket: e.target.value as CoreMarket }))}
                >
                  {Object.values(CoreMarket).map(v => (
                    <option key={v} value={v}>{t(`user.admin.organization.tenants.coreMarket.${v}`, coreMarketLabels[v])}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.dialogField}>
              <label>{t('user.admin.organization.tenants.fields.industrySector', 'Industry sector')}</label>
              <select
                className={styles.dialogInput}
                value={editForm.industrySector}
                onChange={e => setEditForm(f => ({ ...f, industrySector: e.target.value as IndustrySector }))}
              >
                {Object.values(IndustrySector).map(v => (
                  <option key={v} value={v}>{t(`user.admin.organization.tenants.industrySector.${v}`, industrySectorLabels[v])}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className={styles.dialogField}>
                <label>{t('user.admin.organization.tenants.fields.license', 'License')}</label>
                <select
                  className={styles.dialogInput}
                  value={editForm.licenseId ?? ''}
                  onChange={e => setEditForm(f => ({ ...f, licenseId: e.target.value ? Number(e.target.value) : undefined }))}
                >
                  {licenses.map(l => (
                    <option key={l.id} value={l.id}>
                      {l.name || t(`user.admin.organization.tenants.licenseType.${l.licenseType}`, l.licenseType ? licenseTypeLabels[l.licenseType] : '')}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.dialogField}>
                <label>{t('user.admin.organization.tenants.fields.licenseExpiry', 'License expiry date')}</label>
                <input
                  type="date"
                  className={styles.dialogInput}
                  value={editForm.licenseExpirationDate}
                  onChange={e => setEditForm(f => ({ ...f, licenseExpirationDate: e.target.value }))}
                />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                id="tenantEditActive"
                checked={editForm.active}
                onChange={e => setEditForm(f => ({ ...f, active: e.target.checked }))}
                style={{ width: 15, height: 15, cursor: 'pointer' }}
              />
              <label htmlFor="tenantEditActive" style={{ fontSize: 13, fontWeight: 500, cursor: 'pointer', marginBottom: 0 }}>
                {t('user.admin.organization.tenants.activeLabel', 'Active tenant')}
              </label>
            </div>

            <div className={styles.dialogActions}>
              <button className={styles.discardButton} onClick={() => setEditingTenant(null)}>
                {t('user.permissions.edit.discard', 'Cancel')}
              </button>
              <button
                className={styles.saveButton}
                onClick={handleEditSave}
                disabled={!editForm.name.trim() || renameMutation.isPending}
              >
                {renameMutation.isPending
                  ? t('user.admin.organization.tenants.renaming', 'Saving…')
                  : t('user.admin.organization.tenants.renameSave', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteStep1 && (() => {
        const name = (tenants ?? []).find(t => t.id === pendingDeleteId)?.name ?? 'this tenant';
        return (
          <div className={styles.dialogOverlay} onClick={() => { setDeleteStep1(false); setPendingDeleteId(null); }}>
            <div className={styles.dialog} onClick={e => e.stopPropagation()}>
              <h4>Delete tenant?</h4>
              <p style={{ margin: 0, fontSize: 13.5, color: '#525866', lineHeight: 1.6 }}>
                "{name}" and everything inside it — all users, all projects, and all data — will be permanently deleted. This cannot be undone.
              </p>
              <div className={styles.dialogActions}>
                <button className={styles.discardButton} onClick={() => { setDeleteStep1(false); setPendingDeleteId(null); }}>Cancel</button>
                <button className={styles.saveButton} onClick={() => { setDeleteStep1(false); setDeleteStep2(true); }}>Yes, continue</button>
              </div>
            </div>
          </div>
        );
      })()}

      {deleteStep2 && (() => {
        const id = pendingDeleteId;
        const name = (tenants ?? []).find(t => t.id === id)?.name ?? 'this tenant';
        const cancel = () => { setDeleteStep2(false); setPendingDeleteId(null); };
        const confirm = () => {
          if (id == null) return;
          deleteMutation.mutate(
            { id },
            {
              onSuccess: () => {
                setPendingDeleteId(null);
                setDeleteStep2(false);
                refresh();
                showSuccess('Tenant deleted', `"${name}" and all its data have been permanently deleted.`);
              },
              onError: () => showError('Delete failed', 'Could not delete the tenant. Please try again.'),
            }
          );
        };
        return (
          <div className={styles.dialogOverlay} onClick={cancel}>
            <div className={styles.dialog} onClick={e => e.stopPropagation()}>
              <h4>Final confirmation</h4>
              <p style={{ margin: 0, fontSize: 13.5, color: '#525866', lineHeight: 1.6 }}>
                Delete "{name}" permanently. Every user account and project inside it will be destroyed and cannot be recovered.
              </p>
              <div className={styles.dialogActions}>
                <button className={styles.discardButton} onClick={cancel} disabled={deleteMutation.isPending}>Cancel</button>
                <button className={styles.dangerButton} onClick={confirm} disabled={deleteMutation.isPending}>
                  {deleteMutation.isPending ? 'Deleting…' : 'Delete permanently'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const AdminRolesSection: React.FC<{ t: TranslateFn }> = ({ t }) => {
  const { data: tenants } = useGetAdminCustomers();
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');

  const tenantOptions: TenantOption[] = (tenants ?? []).map(ten => ({
    value: ten.tenantId ?? '',
    label: ten.name ?? ten.tenantId ?? '',
  }));

  return (
    <div className={styles.section}>
      <div className={styles.usersTenantBar}>
        <label className={styles.usersTenantLabel}>{t('user.admin.users.tenantLabel', 'Tenant')}</label>
        <TenantSelect
          value={selectedTenantId}
          onChange={setSelectedTenantId}
          options={tenantOptions}
          placeholder={t('user.admin.users.selectTenant', 'Select a tenant…')}
        />
      </div>
      {selectedTenantId
        ? <RolePermissionsMatrix editable canManageRoles adminTenantId={selectedTenantId} />
        : <div className={styles.orgEmpty}>{t('user.admin.roles.selectTenant', 'Select a tenant to view and edit its role matrix.')}</div>
      }
    </div>
  );
};

function tenantUserName(u: TenantUserDto): string {
  return `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || (u.username ?? '?');
}

function tenantUserInitials(u: TenantUserDto): string {
  const f = (u.firstName?.[0] ?? '').toUpperCase();
  const l = (u.lastName?.[0] ?? '').toUpperCase();
  return (f + l) || (u.username?.[0] ?? '?').toUpperCase();
}

const UsersSection: React.FC<{ t: TranslateFn }> = ({ t }) => {
  const queryClient = useQueryClient();
  const { data: tenants } = useGetAdminCustomers();

  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<TenantUserDto | null>(null);
  const [pendingPwReset, setPendingPwReset] = useState<TenantUserDto | null>(null);
  const [newPw, setNewPw] = useState('');
  const [confirmNewPw, setConfirmNewPw] = useState('');
  const [pendingRoleChange, setPendingRoleChange] = useState<{
    userId: number; userName: string; toRoleId: number; toName: string;
  } | null>(null);
  const [createForm, setCreateForm] = useState({
    username: '', password: '', confirmPassword: '',
    firstName: '', lastName: '', tenantRoleId: '', active: true,
  });
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
  const [showCreateConfirm, setShowCreateConfirm] = useState(false);

  const { data: tenantUsers, isLoading: usersLoading } = useGetAdminTenantsTenantIdUsers(selectedTenantId, {
    query: { enabled: !!selectedTenantId },
  });
  const { data: tenantRoles, isLoading: rolesLoading } = useGetAdminTenantsTenantIdRoles(selectedTenantId, {
    query: { enabled: !!selectedTenantId },
  });

  const deleteMutation = useDeleteAdminUsersId();
  const passwordMutation = usePostAdminUsersIdPassword();
  const roleMutation = usePostAdminTenantsTenantIdUsersUserIdRole();
  const createMutation = usePostAdminTenantsTenantIdUsers();

  const refresh = () => {
    if (selectedTenantId) {
      queryClient.invalidateQueries({ queryKey: getGetAdminTenantsTenantIdUsersQueryKey(selectedTenantId) });
    }
  };

  const selectedTenant = (tenants ?? []).find(ten => ten.tenantId === selectedTenantId);

  const handleTenantChange = (tenantId: string) => {
    setSelectedTenantId(tenantId);
    setAddOpen(false);
    setCreateForm({ username: '', password: '', confirmPassword: '', firstName: '', lastName: '', tenantRoleId: '', active: true });
    setCreateErrors({});
  };

  const validateCreateForm = () => {
    const errs: Record<string, string> = {};
    const u = createForm.username.trim();
    if (!u) errs.username = 'Username is required';
    else if (u.length < 3) errs.username = 'Must be at least 3 characters';
    else if (/\s/.test(u)) errs.username = 'Must not contain spaces';
    if (!createForm.password) errs.password = 'Password is required';
    else if (createForm.password.length < 8) errs.password = 'Must be at least 8 characters';
    if (createForm.password !== createForm.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    return errs;
  };

  const doCreate = () => {
    const errs = validateCreateForm();
    if (Object.keys(errs).length > 0) { setCreateErrors(errs); return; }
    setCreateErrors({});
    setShowCreateConfirm(true);
  };

  const doConfirmCreate = () => {
    if (!selectedTenantId) return;
    const resetForm = () => {
      setCreateForm({ username: '', password: '', confirmPassword: '', firstName: '', lastName: '', tenantRoleId: '', active: true });
      setCreateErrors({});
    };
    createMutation.mutate(
      {
        tenantId: selectedTenantId,
        data: {
          username: createForm.username.trim(),
          password: createForm.password,
          firstName: createForm.firstName.trim() || undefined,
          lastName: createForm.lastName.trim() || undefined,
          active: createForm.active,
          tenantRoleId: createForm.tenantRoleId ? Number(createForm.tenantRoleId) : undefined,
        },
      },
      {
        onSuccess: () => {
          setShowCreateConfirm(false);
          setAddOpen(false);
          resetForm();
          refresh();
        },
        onError: () => setShowCreateConfirm(false),
      }
    );
  };

  const tenantOptions: TenantOption[] = (tenants ?? []).map(ten => ({
    value: ten.tenantId ?? '',
    label: ten.name ?? ten.tenantId ?? '',
  }));

  return (
    <div className={styles.section}>
      <div className={styles.usersTenantBar}>
        <label className={styles.usersTenantLabel}>{t('user.admin.users.tenantLabel', 'Tenant')}</label>
        <TenantSelect
          value={selectedTenantId}
          onChange={handleTenantChange}
          options={tenantOptions}
          placeholder={t('user.admin.users.selectTenant', 'Select a tenant…')}
        />
        {selectedTenantId && (
          <button className={styles.btnPrimary} onClick={() => setAddOpen(true)}>
            <IconPlus size={14} />
            {t('user.admin.users.addUser', 'Add user')}
          </button>
        )}
      </div>

      {!selectedTenantId ? (
        <div className={styles.orgEmpty}>
          {t('user.admin.users.selectPrompt', 'Select a tenant above to view and manage its users.')}
        </div>
      ) : usersLoading ? (
        <div className={styles.orgLoading}>{t('user.admin.users.loading', 'Loading users…')}</div>
      ) : (tenantUsers ?? []).length === 0 ? (
        <div className={styles.orgEmpty}>{t('user.admin.users.empty', 'No users in this tenant.')}</div>
      ) : (
        <div className={styles.tenantUserList}>
          <div className={styles.tenantUserHead}>
            <span>{t('user.admin.users.colName', 'Name')}</span>
            <span>{t('user.admin.users.colRole', 'Role')}</span>
            <span />
          </div>
          {(tenantUsers ?? []).map(user => (
            <div key={user.id} className={styles.tenantUserRow}>
              <div className={styles.tenantUserIdent}>
                <span className={styles.adminAvatar}>{tenantUserInitials(user)}</span>
                <div className={styles.adminInfo}>
                  <span className={styles.adminName}>{tenantUserName(user)}</span>
                  <span className={styles.adminUsername}>@{user.username}</span>
                </div>
              </div>
              <div className={styles.tenantUserRole}>
                <select
                  className={`${styles.roleSelectSm} ${user.tenantRoleId == null ? styles.roleSelectSmEmpty : ''}`}
                  value={user.tenantRoleId != null ? String(user.tenantRoleId) : ''}
                  onChange={e => {
                    const toRoleId = parseInt(e.target.value, 10);
                    if (!toRoleId || !user.id || toRoleId === user.tenantRoleId) return;
                    setPendingRoleChange({
                      userId: user.id,
                      userName: tenantUserName(user),
                      toRoleId,
                      toName: (tenantRoles ?? []).find(r => r.id === toRoleId)?.displayName ?? '',
                    });
                  }}
                  disabled={rolesLoading || roleMutation.isPending}
                >
                  <option value="" disabled>{t('user.manageUsers.directory.noRole', 'No role')}</option>
                  {(tenantRoles ?? []).map(role => (
                    <option key={role.id} value={role.id}>{role.displayName}</option>
                  ))}
                </select>
              </div>
              <div className={styles.tenantUserActions}>
                <button
                  className={styles.adminEditButton}
                  onClick={() => { setPendingPwReset(user); setNewPw(''); setConfirmNewPw(''); }}
                  title={t('user.admin.users.resetPw', 'Reset password')}
                >
                  <IconKey size={13} />
                </button>
                <button
                  className={styles.tenantDeleteButton}
                  onClick={() => setPendingDelete(user)}
                  title={t('user.admin.users.deleteUser', 'Remove user')}
                >
                  <IconTrash size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmationDialog
        isOpen={!!pendingRoleChange}
        onClose={() => setPendingRoleChange(null)}
        onConfirm={() => {
          if (!pendingRoleChange || !selectedTenantId) return;
          roleMutation.mutate(
            { tenantId: selectedTenantId, userId: pendingRoleChange.userId, params: { roleId: pendingRoleChange.toRoleId } },
            { onSuccess: () => { refresh(); setPendingRoleChange(null); } }
          );
        }}
        type="confirm"
        title={t('user.admin.users.assignRoleTitle', 'Assign role?')}
        message={t('user.admin.users.assignRoleMsg', {
          user: pendingRoleChange?.userName ?? '',
          role: pendingRoleChange?.toName ?? '',
          defaultValue: 'Assign {{role}} to {{user}}?',
        })}
        confirmText={t('user.admin.users.assignRoleConfirm', 'Assign')}
        cancelText={t('user.admin.users.cancel', 'Cancel')}
      />

      <ConfirmationDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          const uid = pendingDelete?.id;
          if (!uid) return;
          deleteMutation.mutate(
            { id: uid },
            { onSuccess: () => { refresh(); setPendingDelete(null); } }
          );
        }}
        type="danger"
        title={t('user.admin.users.deleteTitle', 'Remove user?')}
        message={t('user.admin.users.deleteMsg', {
          user: pendingDelete ? tenantUserName(pendingDelete) : '',
          defaultValue: 'Permanently remove {{user}} from this tenant? This cannot be undone.',
        })}
        confirmText={t('user.admin.users.deleteConfirm', 'Remove')}
        cancelText={t('user.admin.users.cancel', 'Cancel')}
      />

      {pendingPwReset && (
        <div className={styles.dialogOverlay} onClick={() => { setPendingPwReset(null); setNewPw(''); setConfirmNewPw(''); }}>
          <div className={styles.dialog} onClick={e => e.stopPropagation()}>
            <h4>{t('user.admin.users.resetPwTitle', 'Reset password')} — {tenantUserName(pendingPwReset)}</h4>
            <div className={styles.dialogField}>
              <label>{t('user.admin.users.newPwLabel', 'New password')}</label>
              <input
                className={styles.dialogInput}
                type="password"
                value={newPw}
                onChange={e => setNewPw(e.target.value)}
                placeholder={t('user.admin.users.newPwPlaceholder', 'At least 6 characters')}
                autoFocus
              />
            </div>
            <div className={styles.dialogField}>
              <label>{t('user.admin.users.confirmPwLabel', 'Confirm new password')}</label>
              <input
                className={styles.dialogInput}
                type="password"
                value={confirmNewPw}
                onChange={e => setConfirmNewPw(e.target.value)}
                placeholder={t('user.admin.users.confirmPwPlaceholder', 'Repeat the new password')}
              />
            </div>
            {newPw && confirmNewPw && newPw !== confirmNewPw && (
              <p className={styles.pwError}>{t('user.admin.accounts.passwordMismatch', 'Passwords do not match')}</p>
            )}
            <div className={styles.dialogActions}>
              <button className={styles.discardButton} onClick={() => { setPendingPwReset(null); setNewPw(''); setConfirmNewPw(''); }}>
                {t('user.admin.users.cancel', 'Cancel')}
              </button>
              <button
                className={styles.saveButton}
                onClick={() => {
                  if (!pendingPwReset.id || newPw.length < 6 || newPw !== confirmNewPw) return;
                  passwordMutation.mutate(
                    { id: pendingPwReset.id, data: { password: newPw } },
                    { onSuccess: () => { setPendingPwReset(null); setNewPw(''); setConfirmNewPw(''); } }
                  );
                }}
                disabled={newPw.length < 6 || newPw !== confirmNewPw || passwordMutation.isPending}
              >
                {passwordMutation.isPending
                  ? t('user.admin.users.resetting', 'Resetting…')
                  : t('user.admin.users.resetConfirm', 'Reset password')}
              </button>
            </div>
          </div>
        </div>
      )}

      {addOpen && (
        <div className={styles.dialogOverlay} onClick={() => { setAddOpen(false); setCreateErrors({}); }}>
          <div className={`${styles.dialog} ${styles.dialogLg}`} onClick={e => e.stopPropagation()}>
            <h4>{t('user.admin.users.addTitle', 'Add user')} — {selectedTenant?.name}</h4>
            <div className={styles.accountEditGrid}>
              <div className={styles.dialogField}>
                <label>{t('user.manageUsers.fields.firstName', 'First name')}</label>
                <input
                  className={styles.dialogInput}
                  value={createForm.firstName}
                  onChange={e => setCreateForm(f => ({ ...f, firstName: e.target.value }))}
                />
              </div>
              <div className={styles.dialogField}>
                <label>{t('user.manageUsers.fields.lastName', 'Last name')}</label>
                <input
                  className={styles.dialogInput}
                  value={createForm.lastName}
                  onChange={e => setCreateForm(f => ({ ...f, lastName: e.target.value }))}
                />
              </div>
            </div>
            <div className={styles.dialogField}>
              <label>{t('user.manageUsers.fields.username', 'Username')} *</label>
              <input
                className={`${styles.dialogInput} ${createErrors.username ? styles.dialogInputError : ''}`}
                value={createForm.username}
                onChange={e => { setCreateForm(f => ({ ...f, username: e.target.value })); setCreateErrors(err => ({ ...err, username: '' })); }}
              />
              {createErrors.username && <span className={styles.fieldErrorMsg}>{createErrors.username}</span>}
            </div>
            <div className={styles.accountEditGrid}>
              <div className={styles.dialogField}>
                <label>{t('user.manageUsers.fields.password', 'Password')} *</label>
                <input
                  className={`${styles.dialogInput} ${createErrors.password ? styles.dialogInputError : ''}`}
                  type="password"
                  value={createForm.password}
                  onChange={e => { setCreateForm(f => ({ ...f, password: e.target.value })); setCreateErrors(err => ({ ...err, password: '' })); }}
                  placeholder={t('user.admin.users.newPwPlaceholder', 'At least 8 characters')}
                />
                {createErrors.password && <span className={styles.fieldErrorMsg}>{createErrors.password}</span>}
              </div>
              <div className={styles.dialogField}>
                <label>{t('user.admin.users.confirmPassword', 'Confirm password')} *</label>
                <input
                  className={`${styles.dialogInput} ${createErrors.confirmPassword ? styles.dialogInputError : ''}`}
                  type="password"
                  value={createForm.confirmPassword}
                  onChange={e => { setCreateForm(f => ({ ...f, confirmPassword: e.target.value })); setCreateErrors(err => ({ ...err, confirmPassword: '' })); }}
                />
                {createErrors.confirmPassword && <span className={styles.fieldErrorMsg}>{createErrors.confirmPassword}</span>}
              </div>
            </div>
            <div className={styles.dialogField}>
              <label>{t('user.manageUsers.fields.tenantRole', 'Tenant role')}</label>
              <select
                className={styles.dialogInput}
                value={createForm.tenantRoleId}
                onChange={e => setCreateForm(f => ({ ...f, tenantRoleId: e.target.value }))}
                disabled={rolesLoading}
              >
                <option value="">{t('user.manageUsers.fields.tenantRoleNone', 'No role (assign later)')}</option>
                {(tenantRoles ?? []).map(role => (
                  <option key={role.id} value={role.id}>{role.displayName}</option>
                ))}
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13.5px' }}>
              <input
                type="checkbox"
                checked={createForm.active}
                onChange={e => setCreateForm(f => ({ ...f, active: e.target.checked }))}
              />
              {t('user.admin.users.activeUser', 'Active user')}
            </label>
            <div className={styles.dialogActions}>
              <button className={styles.discardButton} onClick={() => { setAddOpen(false); setCreateErrors({}); }}>
                {t('user.admin.users.cancel', 'Cancel')}
              </button>
              <button
                className={styles.saveButton}
                onClick={doCreate}
                disabled={createMutation.isPending}
              >
                {t('user.manageUsers.create', 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateConfirm && (
        <div className={styles.dialogOverlay} onClick={() => setShowCreateConfirm(false)}>
          <div className={styles.dialog} onClick={e => e.stopPropagation()}>
            <h4>{t('user.manageUsers.confirm.createTitle', 'Create user?')}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', fontSize: '13.5px', margin: '16px 0 24px', padding: '14px 16px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
              <span style={{ color: '#666', whiteSpace: 'nowrap' }}>{t('user.manageUsers.fields.username', 'Username')}</span>
              <span style={{ fontWeight: 600 }}>{createForm.username.trim()}</span>
              {(createForm.firstName || createForm.lastName) && <>
                <span style={{ color: '#666', whiteSpace: 'nowrap' }}>{t('user.manageUsers.fields.firstName', 'Name')}</span>
                <span style={{ fontWeight: 600 }}>{[createForm.firstName.trim(), createForm.lastName.trim()].filter(Boolean).join(' ')}</span>
              </>}
              <span style={{ color: '#666', whiteSpace: 'nowrap' }}>{t('user.manageUsers.fields.tenantRole', 'Tenant role')}</span>
              <span style={{ fontWeight: 600 }}>{createForm.tenantRoleId ? ((tenantRoles ?? []).find(r => String(r.id) === createForm.tenantRoleId)?.displayName ?? createForm.tenantRoleId) : t('user.manageUsers.fields.tenantRoleNone', 'No role')}</span>
              <span style={{ color: '#666', whiteSpace: 'nowrap' }}>{t('user.admin.users.activeUser', 'Active')}</span>
              <span style={{ fontWeight: 600 }}>{createForm.active ? t('user.admin.users.yes', 'Yes') : t('user.admin.users.no', 'No')}</span>
            </div>
            <div className={styles.dialogActions}>
              <button className={styles.discardButton} onClick={() => setShowCreateConfirm(false)}>
                {t('user.admin.users.cancel', 'Cancel')}
              </button>
              <button className={styles.saveButton} onClick={doConfirmCreate} disabled={createMutation.isPending}>
                {createMutation.isPending ? t('user.manageUsers.creating', 'Creating…') : t('user.manageUsers.create', 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function adminInitials(u: UserDto): string {
  const f = (u.firstName?.[0] ?? '').toUpperCase();
  const l = (u.lastName?.[0] ?? '').toUpperCase();
  return (f + l) || (u.username?.[0] ?? '?').toUpperCase();
}

const SystemAccountsSection: React.FC<{ t: TranslateFn }> = ({ t }) => {
  const { data: currentUser } = useGetAuthUser();
  const { data: admins, isLoading } = useGetAdminSystemAdmins();
  const updateMutation = usePutAdminSystemAdminsId();
  const passwordMutation = usePostAuthUserPassword();

  const [editTarget, setEditTarget] = useState<UserDto | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwError, setPwError] = useState('');

  const openEdit = (admin: UserDto) => {
    setEditTarget(admin);
    setFirstName(admin.firstName ?? '');
    setLastName(admin.lastName ?? '');
    setCurrentPw('');
    setNewPw('');
    setConfirmPw('');
    setPwError('');
  };

  const closeEdit = () => setEditTarget(null);

  const saveProfile = () => {
    if (!editTarget?.id) return;
    updateMutation.mutate(
      { id: editTarget.id, data: { ...editTarget, firstName, lastName } },
      { onSuccess: closeEdit }
    );
  };

  const savePassword = () => {
    if (newPw !== confirmPw) { setPwError(t('user.admin.accounts.passwordMismatch', 'Passwords do not match')); return; }
    if (newPw.length < 6) { setPwError(t('user.admin.accounts.passwordTooShort', 'Minimum 6 characters')); return; }
    setPwError('');
    passwordMutation.mutate(
      { data: { current: currentPw, next: newPw } },
      {
        onSuccess: () => { setCurrentPw(''); setNewPw(''); setConfirmPw(''); },
        onError: () => setPwError(t('user.admin.accounts.passwordWrong', 'Current password is incorrect')),
      }
    );
  };

  return (
    <div className={styles.section}>
      <p className={styles.sectionIntroText}>
        {t('user.admin.accounts.description', 'Platform-level system accounts. These are invisible to all tenants and have unrestricted access.')}
      </p>

      {isLoading ? (
        <div className={styles.orgLoading}>{t('user.admin.accounts.loading', 'Loading accounts…')}</div>
      ) : (
        <div className={styles.tenantList}>
          {(admins ?? []).map(admin => {
            const isOwn = admin.id === currentUser?.id;
            return (
              <div key={admin.id} className={styles.adminRow}>
                <div className={styles.adminAvatar}>{adminInitials(admin)}</div>
                <div className={styles.adminInfo}>
                  <span className={styles.adminName}>
                    {admin.firstName} {admin.lastName}
                  </span>
                  <span className={styles.adminUsername}>@{admin.username}</span>
                </div>
                <div className={styles.adminMeta}>
                  <span className={styles.sysAdminBadge}>
                    <IconShieldCheck size={11} />
                    {t('user.admin.accounts.badge', 'System Admin')}
                  </span>
                </div>
                {isOwn && (
                  <button className={styles.adminEditButton} onClick={() => openEdit(admin)}>
                    <IconPencil size={14} />
                  </button>
                )}
                {!isOwn && <div />}
              </div>
            );
          })}
        </div>
      )}

      {editTarget && (
        <div className={styles.dialogOverlay} onClick={closeEdit}>
          <div className={styles.dialog} onClick={e => e.stopPropagation()}>
            <h4>{t('user.admin.accounts.editTitle', 'Edit your account')}</h4>

            <div className={styles.accountEditGrid}>
              <div className={styles.dialogField}>
                <label>{t('user.admin.accounts.firstName', 'First name')}</label>
                <input className={styles.dialogInput} value={firstName} disabled />
              </div>
              <div className={styles.dialogField}>
                <label>{t('user.admin.accounts.lastName', 'Last name')}</label>
                <input className={styles.dialogInput} value={lastName} disabled />
              </div>
            </div>
            <div className={styles.dialogField}>
              <label>{t('user.admin.accounts.usernameLabel', 'Username')}</label>
              <input className={styles.dialogInput} value={editTarget?.username ?? ''} disabled />
            </div>

            <div className={styles.accountDivider} />

            <h4>{t('user.admin.accounts.changePassword', 'Change password')}</h4>
            <div className={styles.dialogField}>
              <label>{t('user.admin.accounts.currentPw', 'Current password')}</label>
              <input className={styles.dialogInput} type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} />
            </div>
            <div className={styles.accountEditGrid}>
              <div className={styles.dialogField}>
                <label>{t('user.admin.accounts.newPw', 'New password')}</label>
                <input className={styles.dialogInput} type="password" value={newPw} onChange={e => setNewPw(e.target.value)} />
              </div>
              <div className={styles.dialogField}>
                <label>{t('user.admin.accounts.confirmPw', 'Confirm new password')}</label>
                <input className={styles.dialogInput} type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} />
              </div>
            </div>
            {pwError && <p className={styles.pwError}>{pwError}</p>}

            <div className={styles.dialogActions}>
              <button
                className={styles.saveButton}
                onClick={savePassword}
                disabled={passwordMutation.isPending || !currentPw || !newPw || !confirmPw}
              >
                {passwordMutation.isPending
                  ? t('user.admin.accounts.saving', 'Saving…')
                  : t('user.admin.accounts.setPassword', 'Update password')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TemplatesSection: React.FC<{ t: TranslateFn }> = ({ t }) => {
  const categories = [
    { key: 'projectTypes', label: t('user.admin.templates.projectTypes') },
    { key: 'characters', label: t('user.admin.templates.characters') },
    { key: 'phaseTemplates', label: t('user.admin.templates.phaseTemplates') },
    { key: 'checklistTemplates', label: t('user.admin.templates.checklistTemplates') },
  ];

  return (
    <div className={styles.section}>
      <div className={styles.sectionIntro}>
        <p>{t('user.admin.templates.description')}</p>
      </div>

      <div className={styles.templateList}>
        {categories.map((cat) => (
          <div key={cat.key} className={styles.templateRow}>
            <div className={styles.templateInfo}>
              <span className={styles.templateLabel}>{cat.label}</span>
              <span className={styles.templateCount}>—</span>
            </div>
            <button className={styles.btnSecondary} disabled>{t('user.admin.common.configure')}</button>
          </div>
        ))}
      </div>
    </div>
  );
};

const ParametersSection: React.FC<{ t: TranslateFn }> = ({ t }) => {
  const params = [
    { key: 'timingFactors', label: t('user.admin.parameters.timingFactors') },
    { key: 'complexityLevels', label: t('user.admin.parameters.complexityLevels') },
    { key: 'sprintFrequencies', label: t('user.admin.parameters.sprintFrequencies') },
    { key: 'milestoneTypes', label: t('user.admin.parameters.milestoneTypes') },
    { key: 'releaseCategories', label: t('user.admin.parameters.releaseCategories') },
  ];

  return (
    <div className={styles.section}>
      <div className={styles.sectionIntro}>
        <p>{t('user.admin.parameters.description')}</p>
      </div>

      <div className={styles.paramList}>
        {params.map((p) => (
          <div key={p.key} className={styles.paramRow}>
            <span className={styles.paramLabel}>{p.label}</span>
            <div className={styles.paramActions}>
              <span className={styles.paramEntries}>—</span>
              <button className={styles.btnSecondary} disabled>{t('user.admin.common.edit')}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const LicenseSection: React.FC<{ t: TranslateFn }> = ({t}) => {
    const queryClient = useQueryClient();
    const {showSuccess, showError} = useNotifications();
    const {isSystemAdmin} = useCurrentUserPermissions();

    const {data: licenses = [], isLoading} = useGetAdminLicenses();
    const createMutation = usePostAdminLicenses();
    const updateMutation = usePutAdminLicensesId();
    const deleteMutation = useDeleteAdminLicensesId();

    const [licenseModal, setLicenseModal] = useState<{
        mode: 'create' | 'edit';
        form: Partial<LicenseDto>
    } | null>(null);
    const [pendingDeleteLicense, setPendingDeleteLicense] = useState<LicenseDto | null>(null);

    const refresh = () => queryClient.invalidateQueries({queryKey: getGetAdminLicensesQueryKey()});

    const canEdit = isSystemAdmin;
    const licenseLabel = (license?: Partial<LicenseDto> | null) =>
        license?.name || license?.licenseType || t('user.admin.licenses.fallbackLicense', 'License');
    const licenseTypeLabel = (type?: LicenseType | null) =>
        type ? t(`user.admin.organization.tenants.licenseType.${type}`, licenseTypeLabels[type]) : '—';
    const formatQuota = (value?: number | null) => {
        if (value == null || value < 0) return t('user.admin.licenses.unlimited', 'Unlimited');
        return new Intl.NumberFormat().format(value);
    };
    const licenseMetrics = (license: Partial<LicenseDto>) => [
        {label: t('user.admin.licenses.metrics.users', 'users'), value: formatQuota(license.maxUsers)},
        {label: t('user.admin.licenses.metrics.projects', 'projects'), value: formatQuota(license.maxProjects)},
    ];

    const openCreate = () => {
        if (!canEdit) return;
        setLicenseModal({
            mode: 'create',
            form: {
                licenseType: LicenseType.BASIC,
                active: true,
            },
        });
    };

    const saveLicense = () => {
        if (!licenseModal) return;
        const form = licenseModal.form as LicenseDto;
        if (licenseModal.mode === 'create') {
            createMutation.mutate({data: form}, {
                onSuccess: () => {
                    setLicenseModal(null);
                    refresh();
                    showSuccess(
                        t('user.admin.licenses.feedback.createdTitle', 'License created'),
                        t('user.admin.licenses.feedback.createdMessage', {
                            defaultValue: 'License "{{name}}" added.',
                            name: licenseLabel(form),
                        })
                    );
                },
                onError: () => showError(
                    t('user.admin.licenses.feedback.createErrorTitle', 'Create failed'),
                    t('user.admin.licenses.feedback.createErrorMessage', 'Could not create the license.')
                ),
            });
        } else {
            updateMutation.mutate({id: form.id!, data: form}, {
                onSuccess: () => {
                    setLicenseModal(null);
                    refresh();
                    showSuccess(
                        t('user.admin.licenses.feedback.updatedTitle', 'License updated'),
                        t('user.admin.licenses.feedback.updatedMessage', {
                            defaultValue: 'License "{{name}}" updated.',
                            name: licenseLabel(form),
                        })
                    );
                },
                onError: () => showError(
                    t('user.admin.licenses.feedback.updateErrorTitle', 'Update failed'),
                    t('user.admin.licenses.feedback.updateErrorMessage', 'Could not update the license.')
                ),
            });
        }
    };

    return (
        <div className={styles.section}>
            <div className={styles.orgHead}>
                <p className={styles.sectionIntroText}>
                    {canEdit
                        ? t('user.admin.licenses.descriptionEditable', 'Global license definitions. Only system admins can create, edit, or delete these records.')
                        : t('user.admin.licenses.descriptionReadOnly', 'Read-only view of global license definitions.')}
                </p>
                {canEdit && (
                    <button className={styles.btnPrimary} onClick={openCreate}>
                        <IconPlus size={14}/>
                        {t('user.admin.licenses.new', 'New license')}
                    </button>
                )}
            </div>

            {!canEdit && (
                <div className={styles.warningBanner}>
                    <IconLock size={14}/>
                    <span>{t('user.admin.licenses.readOnlyWarning', 'License records can only be edited by system admins.')}</span>
                </div>
            )}

            {isLoading ? (
                <div className={styles.orgLoading}>{t('user.admin.licenses.loading', 'Loading licenses…')}</div>
            ) : (
                <div className={styles.licenseList}>
                    {(licenses ?? []).map(license => (
                        <div key={license.id} className={styles.licenseRow}>
                            <div className={styles.licenseMain}>
                                <div className={styles.licenseHead}>
                                    <div className={styles.licenseTitleWrap}>
                    <span className={styles.licensePlanBadge}>
                      {licenseTypeLabel(license.licenseType)}
                    </span>
                                        <span
                                            className={styles.adminName}>{license.name || licenseLabel(license)}</span>
                                    </div>
                                    <span
                                        className={`${styles.licenseStatusBadge} ${license.active ? styles.licenseStatusActive : styles.licenseStatusInactive}`}>
                    {license.active
                        ? t('user.admin.licenses.status.active', 'Active')
                        : t('user.admin.licenses.status.inactive', 'Inactive')}
                  </span>
                                </div>
                                <div className={styles.licenseMeta}>
                                    {licenseMetrics(license).map(metric => (
                                        <span key={metric.label} className={styles.licenseMetricChip}>
                      <span className={styles.licenseMetricLabel}>{metric.label}</span>
                      <span className={styles.licenseMetricValue}>{metric.value}</span>
                    </span>
                                    ))}
                                </div>
                                {(license.featureSet || license.description) && (
                                    <div className={styles.licenseCopy}>
                                        {license.featureSet && (
                                            <div className={styles.licenseCopyLine}>
                                                <span
                                                    className={styles.licenseCopyLabel}>{t('user.admin.licenses.fields.featureSet', 'Feature set')}</span>
                                                <span className={styles.licenseCopyValue}>{license.featureSet}</span>
                                            </div>
                                        )}
                                        {license.description && (
                                            <div className={styles.licenseCopyLine}>
                                                <span
                                                    className={styles.licenseCopyLabel}>{t('user.admin.licenses.fields.description', 'Description')}</span>
                                                <span className={styles.licenseCopyValue}>{license.description}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className={styles.licenseActions}>
                                {canEdit ? (
                                    <>
                                        <button
                                            className={styles.adminEditButton}
                                            onClick={() => setLicenseModal({mode: 'edit', form: {...license}})}
                                            title={t('user.admin.licenses.actions.edit', 'Edit license')}
                                        >
                                            <IconPencil size={14}/>
                                        </button>
                                        <button
                                            className={styles.tenantDeleteButton}
                                            onClick={() => setPendingDeleteLicense(license)}
                                            title={t('user.admin.licenses.actions.delete', 'Delete license')}
                                        >
                                            <IconTrash size={14}/>
                                        </button>
                                    </>
                                ) : (
                                    <div/>
                                )}
                            </div>
                        </div>
                    ))}
                    {(licenses ?? []).length === 0 && (
                        <div className={styles.orgEmpty}>{t('user.admin.licenses.empty', 'No licenses yet.')}</div>
                    )}
                </div>
            )}

            {licenseModal && (
                <div className={styles.dialogOverlay} onClick={() => setLicenseModal(null)}>
                    <div className={styles.dialog} style={{width: 560}} onClick={e => e.stopPropagation()}>
                        <h4>{licenseModal.mode === 'create'
                            ? t('user.admin.licenses.modal.createTitle', 'Create license')
                            : t('user.admin.licenses.modal.editTitle', 'Edit license')}</h4>
                        <div className={styles.dialogField}>
                            <label>{t('user.admin.licenses.fields.name', 'Name')}</label>
                            <input
                                className={styles.dialogInput}
                                value={licenseModal.form.name ?? ''}
                                onChange={e => setLicenseModal(m => m ? {
                                    ...m,
                                    form: {...m.form, name: e.target.value}
                                } : m)}
                            />
                        </div>
                        <div className={styles.dialogField}>
                            <label>{t('user.admin.licenses.fields.licenseType', 'License type')}</label>
                            <select
                                className={styles.dialogInput}
                                value={licenseModal.form.licenseType ?? LicenseType.BASIC}
                                onChange={e => setLicenseModal(m => m ? {
                                    ...m,
                                    form: {...m.form, licenseType: e.target.value as LicenseType}
                                } : m)}
                            >
                                {Object.values(LicenseType).map(v => (
                                    <option key={v} value={v}>{licenseTypeLabel(v)}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
                            <div className={styles.dialogField}>
                                <label>{t('user.admin.licenses.fields.maxUsers', 'Max users')}</label>
                                <input
                                    type="number"
                                    min={0}
                                    className={styles.dialogInput}
                                    value={licenseModal.form.maxUsers ?? ''}
                                    onChange={e => setLicenseModal(m => m ? {
                                        ...m,
                                        form: {...m.form, maxUsers: e.target.value ? Number(e.target.value) : undefined}
                                    } : m)}
                                />
                            </div>
                            <div className={styles.dialogField}>
                                <label>{t('user.admin.licenses.fields.maxProjects', 'Max projects')}</label>
                                <input
                                    type="number"
                                    min={0}
                                    className={styles.dialogInput}
                                    value={licenseModal.form.maxProjects ?? ''}
                                    onChange={e => setLicenseModal(m => m ? {
                                        ...m,
                                        form: {
                                            ...m.form,
                                            maxProjects: e.target.value ? Number(e.target.value) : undefined
                                        }
                                    } : m)}
                                />
                            </div>
                        </div>
                        <div className={styles.dialogField}>
                            <label>{t('user.admin.licenses.fields.featureSet', 'Feature set')}</label>
                            <input
                                className={styles.dialogInput}
                                value={licenseModal.form.featureSet ?? ''}
                                onChange={e => setLicenseModal(m => m ? {
                                    ...m,
                                    form: {...m.form, featureSet: e.target.value}
                                } : m)}
                            />
                        </div>
                        <div className={styles.dialogField}>
                            <label>{t('user.admin.licenses.fields.description', 'Description')}</label>
                            <textarea
                                className={`${styles.dialogInput} ${styles.modalTextarea}`}
                                value={licenseModal.form.description ?? ''}
                                onChange={e => setLicenseModal(m => m ? {
                                    ...m,
                                    form: {...m.form, description: e.target.value}
                                } : m)}
                            />
                        </div>
                        <label
                            style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13.5}}>
                            <input
                                type="checkbox"
                                checked={licenseModal.form.active ?? true}
                                onChange={e => setLicenseModal(m => m ? {
                                    ...m,
                                    form: {...m.form, active: e.target.checked}
                                } : m)}
                            />
                            {t('user.admin.licenses.fields.active', 'Active')}
                        </label>
                        <div className={styles.dialogActions}>
                            <button className={styles.discardButton} onClick={() => setLicenseModal(null)}>
                                {t('user.admin.licenses.actions.cancel', 'Cancel')}
                            </button>
                            <button className={styles.saveButton} onClick={saveLicense}>
                                {licenseModal.mode === 'create'
                                    ? t('user.admin.licenses.actions.create', 'Create')
                                    : t('user.admin.licenses.actions.save', 'Save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmationDialog
                isOpen={!!pendingDeleteLicense}
                onClose={() => setPendingDeleteLicense(null)}
                onConfirm={() => {
                    if (!pendingDeleteLicense?.id) return;
                    deleteMutation.mutate(
                        {id: pendingDeleteLicense.id},
                        {
                            onSuccess: () => {
                                setPendingDeleteLicense(null);
                                refresh();
                                showSuccess(
                                    t('user.admin.licenses.feedback.deletedTitle', 'License deleted'),
                                    t('user.admin.licenses.feedback.deletedMessage', {
                                        defaultValue: 'License "{{name}}" removed.',
                                        name: licenseLabel(pendingDeleteLicense),
                                    })
                                );
                            },
                            onError: () => showError(
                                t('user.admin.licenses.feedback.deleteErrorTitle', 'Delete failed'),
                                t('user.admin.licenses.feedback.deleteErrorMessage', 'Could not delete the license.')
                            ),
                        }
                    );
                }}
                type="danger"
                title={t('user.admin.licenses.deleteDialog.title', 'Delete license?')}
                message={t('user.admin.licenses.deleteDialog.message', {
                    defaultValue: 'Remove "{{name}}"? This cannot be undone.',
                    name: licenseLabel(pendingDeleteLicense),
                })}
                confirmText={t('user.admin.licenses.actions.deleteConfirm', 'Delete')}
                cancelText={t('user.admin.licenses.actions.cancel', 'Cancel')}
            />
    </div>
  );
};

const SOON_SECTIONS = new Set<AdminSection>(['templates', 'parameters']);

const AdminConsole: React.FC = () => {
  const { t } = useTranslation('settings');
  const [activeSection, setActiveSection] = useState<AdminSection>('overview');
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      nav.scrollLeft += e.deltaY;
    };
    nav.addEventListener('wheel', onWheel, { passive: false });
    return () => nav.removeEventListener('wheel', onWheel);
  }, []);

  const navItems: { id: AdminSection; icon: React.ReactNode; soon?: boolean }[] = [
    { id: 'overview', icon: <IconShieldLock size={14} /> },
    { id: 'roles', icon: <IconKey size={14} /> },
    { id: 'users', icon: <IconUsers size={14} /> },
    { id: 'organization', icon: <IconBuildingStore size={14} /> },
    { id: 'accounts', icon: <IconShieldCheck size={14} /> },
    { id: 'feedback', icon: <IconMessage2 size={14} /> },
      {id: 'licenses', icon: <IconLock size={14}/>},
    { id: 'catalog', icon: <IconBook2 size={14} /> },
    { id: 'templates', icon: <IconTemplate size={14} />, soon: true },
    { id: 'parameters', icon: <IconAdjustments size={14} />, soon: true },
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'overview': return <OverviewSection t={t} onNavigate={setActiveSection} />;
      case 'roles': return <AdminRolesSection t={t} />;
      case 'users': return <UsersSection t={t} />;
      case 'organization': return <OrganizationSection t={t} />;
        case 'licenses':
            return <LicenseSection t={t}/>;
      case 'accounts': return <SystemAccountsSection t={t} />;
      case 'feedback': return <AdminFeedbackSection />;
      case 'catalog': return <CatalogSection t={t} />;
      case 'templates': return <TemplatesSection t={t} />;
      case 'parameters': return <ParametersSection t={t} />;
    }
  };

  const isSoon = SOON_SECTIONS.has(activeSection);

  return (
    <div className={styles.console}>
      <div className={styles.consoleHead}>
        <div className={styles.headInfo}>
          <IconShieldLock size={16} className={styles.headIcon} />
          <div className={styles.headText}>
            <span className={styles.consoleTitle}>{t('user.admin.title')}</span>
            <span className={styles.consoleSubtitle}>{t('user.admin.subtitle')}</span>
          </div>
        </div>
        <div className={styles.headBadge}>
          <IconAlertTriangle size={11} />
          <span>{t('user.admin.superAdminOnly')}</span>
        </div>
      </div>

      <nav className={styles.consoleNav} ref={navRef}>
        {navItems.map(({ id, icon, soon }) => (
          <button
            key={id}
            className={`${styles.navTab} ${activeSection === id ? styles.navTabActive : ''}`}
            onClick={() => setActiveSection(id)}
            title={t(`user.admin.nav.${id}`)}
          >
            <span className={styles.navTabIcon}>{icon}</span>
            <span className={styles.navTabLabel}>{t(`user.admin.nav.${id}`)}</span>
            {soon && <span className={styles.navTabSoonBadge}>Soon</span>}
          </button>
        ))}
      </nav>

      <div className={styles.soonWrapper}>
        <div className={`${styles.consoleContent} ${isSoon ? styles.consoleContentBlurred : ''}`}>
          {renderSection()}
        </div>
        {isSoon && (
          <div className={styles.soonOverlay}>
            <div className={styles.soonCard}>
              <div className={styles.soonCardIcon}><IconLock size={38} /></div>
              <div className={styles.soonCardTitle}>Coming Soon</div>
              <div className={styles.soonCardDesc}>
                This feature is under active development and will be available in a future release.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminConsole;
